import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import PasswordInput from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { MessageEditor } from "@/components/MessageEditor";
import { ImportReviewQueue } from "@/components/ImportReviewQueue";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Shield, Users as UsersIcon, KeyRound, Ticket, BarChart3, Copy,
  RefreshCw, UserPlus, Trash2, PowerOff, Power, ArrowLeft, Mail, Send, Check, Clock,
  ChevronDown, ChevronRight, MessageSquare, PenSquare, Mic, Users, DollarSign, CalendarDays, Timer, Activity,
  Phone, Upload, FileText, Download,
} from "lucide-react";

const STATUS_COLORS = {
  free: "bg-slate-100 text-slate-700",
  trial: "bg-amber-100 text-amber-900",
  comp: "bg-purple-100 text-purple-900",
  paid: "bg-green-100 text-green-900",
};

// Mirror of the caller-side status label map so admins see the same wording.
const STATUS_LABEL_MAP = {
  not_contacted: "Not called yet",
  reached: "Reached",
  left_message: "Left message",
  pledged: "Pledged",
  declined: "Declined",
  call_back: "Follow up",
};

const FEATURE_LABELS = {
  post_generated: "Posts generated",
  post_refined: "Post edits",
  post_saved: "Posts saved",
  speech_generated: "Speeches",
  speech_refined: "Speech edits",
  advisor_chat: "Advisor chats",
  voter_uploaded: "Voter uploads",
  voter_query: "Voter searches",
  voter_export: "Voter exports",
  voter_walk_book: "Walk-book PDFs",
  voter_import: "Voter imports",
  calendar_event_created: "Calendar events",
  checklist_updated: "Checklist toggles",
  graphic_rendered: "Graphics",
  campaign_imported: "AI imports",
  volunteer_created: "Volunteers added",
  volunteer_updated: "Volunteer edits",
  lawn_sign_created: "Lawn sign requests",
  lawn_sign_updated: "Lawn sign updates",
  finance_contribution_added: "Contributions added",
  finance_expense_added: "Expenses added",
  finance_report_created: "Finance reports",
  finance_csv_imported: "Finance CSV imports",
  login: "Logins",
};

// Feature category groupings for the per-user drawer, matching the tools users
// see in the app navigation. Ordering here drives the display order.
const FEATURE_GROUPS = [
  { key: "advisor",   label: "Advisor",        icon: MessageSquare, features: ["advisor_chat"] },
  { key: "posts",     label: "Post Generator", icon: PenSquare,     features: ["post_generated", "post_refined", "post_saved"] },
  { key: "speeches",  label: "Speech Writer",  icon: Mic,           features: ["speech_generated", "speech_refined"] },
  { key: "voters",    label: "Voter List",     icon: Users,         features: ["voter_uploaded", "voter_query", "voter_export", "voter_walk_book", "voter_import"] },
  { key: "finances",  label: "Finances",       icon: DollarSign,    features: ["finance_contribution_added", "finance_expense_added", "finance_report_created", "finance_csv_imported"] },
];

// Append a cache-buster to GET URLs so the browser never serves a stale
// response from its HTTP cache. Combined with the backend's `Cache-Control:
// no-store` header on /api/admin/*, this guarantees every Refresh fetches
// fresh data.
function bust(url) {
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}_t=${Date.now()}`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return iso; }
}

function daysLeftFrom(iso) {
  if (!iso) return null;
  try {
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return null;
    const diffMs = t - Date.now();
    if (diffMs <= 0) return null;
    return Math.ceil(diffMs / 86400000);
  } catch {
    return null;
  }
}

function prettyTier(t) {
  if (!t || typeof t !== "string") return "";
  return t.charAt(0).toUpperCase() + t.slice(1).toLowerCase();
}

export default function Admin() {
  const navigate = useNavigate();
  const [authorized, setAuthorized] = useState(null); // null=checking, true|false
  const [tab, setTab] = useState("accounts");

  useEffect(() => {
    api.get("/admin/me")
      .then(() => setAuthorized(true))
      .catch(() => setAuthorized(false));
  }, []);

  if (authorized === null) return <div className="max-w-6xl mx-auto px-4 py-10 text-slate-500" data-testid="admin-loading">Checking access…</div>;
  if (!authorized) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center" data-testid="admin-denied">
        <Shield className="h-10 w-10 text-flag-red mx-auto" />
        <h1 className="font-display text-3xl font-black text-flag-navy mt-3">Owner only</h1>
        <p className="text-slate-600 mt-2">This console is restricted to the owner account.</p>
        <Button onClick={() => navigate("/dashboard")} className="mt-5 h-11 px-5 bg-flag-red hover:bg-flag-red-hover text-white font-bold">
          <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to dashboard
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10" data-testid="admin-root">
      <div>
        <p className="text-sm font-semibold uppercase tracking-wider text-flag-red flex items-center gap-2"><Shield className="h-3 w-3" /> Owner console</p>
        <h1 className="font-display text-3xl sm:text-4xl font-black text-flag-navy mt-1">Admin</h1>
        <p className="text-slate-600 mt-2 max-w-2xl">Account management, usage analytics, comp/trial codes, and pending password-reset links — all server-enforced to your account.</p>
      </div>

      <div className="mt-6 inline-flex bg-slate-100 rounded-lg p-1" data-testid="admin-tabs">
        {[
          { v: "accounts", label: "Accounts", icon: UsersIcon },
          { v: "analytics", label: "Analytics", icon: BarChart3 },
          { v: "callers", label: "Callers", icon: Phone },
          { v: "codes", label: "Codes", icon: Ticket },
          { v: "invites", label: "Invites", icon: Mail },
          { v: "resets", label: "Reset links", icon: KeyRound },
          { v: "legal", label: "Legal", icon: FileText },
        ].map(({ v, label, icon: Icon }) => (
          <button key={v} type="button" onClick={() => setTab(v)} data-testid={`admin-tab-${v}`} className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold rounded-md transition-colors ${tab === v ? "bg-white text-flag-navy shadow-sm" : "text-slate-600 hover:text-flag-navy"}`}>
            <Icon className="h-3.5 w-3.5" /> {label}
          </button>
        ))}
      </div>

      <div className="mt-6">
        {tab === "accounts" && <AccountsTab />}
        {tab === "analytics" && <AnalyticsTab />}
        {tab === "callers" && <CallersTab />}
        {tab === "codes" && <CodesTab />}
        {tab === "invites" && <InvitesTab />}
        {tab === "resets" && <ResetsTab />}
        {tab === "legal" && <LegalTab />}
      </div>
    </div>
  );
}

function copyToClipboard(text) {
  try {
    navigator.clipboard.writeText(text);
    toast.success("Copied");
  } catch {
    toast.error("Couldn't copy — select manually.");
  }
}

/* ---------------- Accounts ---------------- */
function AccountsTab() {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedUserId, setExpandedUserId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [lastLoadedAt, setLastLoadedAt] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(bust(`/admin/users${q ? `?q=${encodeURIComponent(q)}` : ""}`));
      setRows(data || []);
      setLastLoadedAt(new Date());
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };
  // Mount-only fetch by design: subsequent loads are user-driven (search
  // button, auto-refresh interval below). `load` is deliberately omitted so
  // typing in the search box never fires un-debounced requests.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  // Auto-refresh while the toggle is on: re-pull every 30s. Cleared on toggle
  // off, unmount, or tab change. `q` keeps the interval closure fresh; `load`
  // itself is stable behavior-wise, so it's intentionally not a dep.
  useEffect(() => {
    if (!autoRefresh) return undefined;
    const id = setInterval(load, 30_000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh, q]);

  const resetPassword = async (u) => {
    try {
      const { data } = await api.post(`/admin/users/${u.id}/reset-password`);
      copyToClipboard(data.temp_password);
      toast.success(`Temp password for ${u.email}: ${data.temp_password} (copied)`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const toggleDeactivate = async (u) => {
    try {
      await api.patch(`/admin/users/${u.id}`, { deactivated: !u.deactivated });
      toast.success(u.deactivated ? "Reactivated" : "Deactivated");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const setComp = async (u, revoked) => {
    try {
      await api.post(`/admin/users/${u.id}/comp`, { revoked });
      toast.success(revoked ? "Comp revoked" : "Comp restored");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const updateStatus = async (u, status) => {
    try {
      await api.patch(`/admin/users/${u.id}`, { status });
      toast.success(`Status: ${status}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
        <div className="flex items-center gap-2">
          <Input placeholder="Search name or email" value={q} onChange={(e) => setQ(e.target.value)} className="h-10 w-72" data-testid="admin-users-search" onKeyDown={(e) => e.key === "Enter" && load()} />
          <Button onClick={load} variant="outline" className="border-2 font-semibold h-10" data-testid="admin-users-search-btn">Search</Button>
          <Button onClick={load} variant="outline" className="border-2 font-semibold h-10" data-testid="admin-users-refresh-btn" disabled={loading} title="Re-pull live from the database">
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <label className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 cursor-pointer select-none" data-testid="admin-users-autorefresh-label">
            <input type="checkbox" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} data-testid="admin-users-autorefresh" />
            Auto-refresh 30s
          </label>
        </div>
        <Button onClick={() => setCreateOpen(true)} className="bg-flag-red hover:bg-flag-red-hover text-white font-bold h-10" data-testid="admin-create-user-btn">
          <UserPlus className="h-4 w-4 mr-1.5" /> Create account
        </Button>
      </div>
      <div className="mb-3 text-xs text-slate-500" data-testid="admin-users-meta">
        {rows.length.toLocaleString()} {rows.length === 1 ? "account" : "accounts"} loaded
        {lastLoadedAt && <> &middot; live from database at <span className="font-semibold text-slate-700">{lastLoadedAt.toLocaleTimeString()}</span></>}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid="admin-users-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
              <tr>
                <th className="text-left px-4 py-3 font-bold">Name / Email</th>
                <th className="text-left px-3 py-3 font-bold">Type</th>
                <th className="text-left px-3 py-3 font-bold">Status</th>
                <th className="text-left px-3 py-3 font-bold">Joined</th>
                <th className="text-left px-3 py-3 font-bold">Logins</th>
                <th className="text-left px-3 py-3 font-bold">Last login</th>
                <th className="text-right px-3 py-3 font-bold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} className="text-center py-6 text-slate-500">Loading…</td></tr>}
              {!loading && rows.map((u) => {
                const isOpen = expandedUserId === u.id;
                return (
                <Fragment key={u.id}>
                <tr className={`border-t border-slate-100 hover:bg-slate-50/60 ${u.deactivated ? "opacity-60" : ""} ${isOpen ? "bg-slate-50/80" : ""}`} data-testid={`admin-user-row-${u.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-flag-navy">{u.name || "—"}</div>
                    <div className="text-xs text-slate-500">{u.email}</div>
                  </td>
                  <td className="px-3 py-3 text-slate-700">{u.account_type || "—"}</td>
                  <td className="px-3 py-3">
                    <div className="flex flex-col gap-1">
                      <Select value={u.status} onValueChange={(v) => updateStatus(u, v)}>
                        <SelectTrigger data-testid={`admin-user-status-${u.id}`} className={`h-8 text-xs font-semibold ${STATUS_COLORS[u.status] || ""} border-transparent`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {["free", "trial", "comp", "paid"].map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
                        </SelectContent>
                      </Select>
                      {u.comp && u.comp.code && (
                        <div className="inline-flex items-center gap-1.5">
                          <span
                            className={`inline-flex items-center text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${u.comp.revoked ? "bg-slate-200 text-slate-600 line-through" : "bg-emerald-100 text-emerald-900"}`}
                            title={u.comp.revoked ? `Revoked` : `Active comp`}
                          >
                            {u.comp.code}
                          </span>
                          <button
                            type="button"
                            onClick={() => setComp(u, !u.comp.revoked)}
                            data-testid={`admin-user-comp-toggle-${u.id}`}
                            className="text-[10px] font-semibold text-slate-600 hover:text-flag-red"
                          >
                            {u.comp.revoked ? "Restore" : "Revoke"}
                          </button>
                        </div>
                      )}
                      {u.subscription && u.subscription.tier && (
                        <div className="inline-flex flex-wrap items-center gap-1.5">
                          <span
                            className="inline-flex items-center text-[10px] font-bold uppercase tracking-wider bg-sky-100 text-sky-900 px-1.5 py-0.5 rounded"
                            title={`Stripe sub ${u.subscription.status}${u.subscription.cancel_at_period_end ? " (cancels at period end)" : ""}`}
                            data-testid={`admin-user-subscription-${u.id}`}
                          >
                            {prettyTier(u.subscription.tier)} · {u.subscription.status}
                          </span>
                          {u.subscription.cancel_at_period_end && (
                            <span
                              className="inline-flex items-center text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-900 px-1.5 py-0.5 rounded"
                              title={u.subscription.current_period_end ? `Cancels ${fmtDate(u.subscription.current_period_end)}` : "Cancels at period end"}
                              data-testid={`admin-user-cancels-${u.id}`}
                            >
                              Cancels
                            </span>
                          )}
                        </div>
                      )}
                      {(() => {
                        const days = daysLeftFrom(u.trial_ends_at);
                        if (days === null) return null;
                        return (
                          <span
                            className="inline-flex items-center text-[10px] font-bold uppercase tracking-wider bg-indigo-100 text-indigo-900 px-1.5 py-0.5 rounded self-start"
                            title={`Trial ends ${fmtDate(u.trial_ends_at)}`}
                            data-testid={`admin-user-trial-days-${u.id}`}
                          >
                            Trial · {days} {days === 1 ? "day" : "days"} left
                          </span>
                        );
                      })()}
                    </div>
                  </td>
                  <td className="px-3 py-3 text-slate-700 whitespace-nowrap">{fmtDate(u.created_at)}</td>
                  <td className="px-3 py-3 text-slate-700">{u.login_count}</td>
                  <td className="px-3 py-3 text-slate-700 whitespace-nowrap">{fmtDate(u.last_login_at)}</td>
                  <td className="px-3 py-3 text-right whitespace-nowrap">
                    <div className="inline-flex items-center gap-1">
                      <Button
                        onClick={() => setExpandedUserId(isOpen ? null : u.id)}
                        variant="outline"
                        className="h-8 px-2 text-xs border-2 font-semibold"
                        data-testid={`admin-user-details-${u.id}`}
                        aria-expanded={isOpen}
                      >
                        {isOpen ? <ChevronDown className="h-3 w-3 mr-1" /> : <ChevronRight className="h-3 w-3 mr-1" />}
                        Usage
                      </Button>
                      <Button onClick={() => resetPassword(u)} variant="outline" className="h-8 px-2 text-xs border-2 font-semibold" data-testid={`admin-user-reset-${u.id}`}>
                        <RefreshCw className="h-3 w-3 mr-1" /> Reset PW
                      </Button>
                      <Button onClick={() => toggleDeactivate(u)} variant="outline" className="h-8 px-2 text-xs border-2 font-semibold" data-testid={`admin-user-deactivate-${u.id}`}>
                        {u.deactivated ? <Power className="h-3 w-3 mr-1" /> : <PowerOff className="h-3 w-3 mr-1" />}
                        {u.deactivated ? "Reactivate" : "Deactivate"}
                      </Button>
                      <DeleteUserButton user={u} onDeleted={load} />
                    </div>
                  </td>
                </tr>
                {isOpen && (
                  <tr key={`${u.id}-drawer`} className="border-t border-slate-100 bg-slate-50/80">
                    <td colSpan={7} className="px-4 py-4">
                      <UserUsageDrawer user={u} />
                    </td>
                  </tr>
                )}
                </Fragment>
                );
              })}
              {!loading && rows.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-slate-500">No accounts.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <CreateUserDialog open={createOpen} setOpen={setCreateOpen} onCreated={load} />
    </div>
  );
}

function DeleteUserButton({ user, onDeleted }) {
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);

  const openIt = async () => {
    setOpen(true);
    setPreview(null);
    try {
      const { data } = await api.get(`/admin/users/${user.id}/preview-delete`);
      setPreview(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setOpen(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/admin/users/${user.id}?confirm=DELETE`);
      toast.success(`${user.email} deleted`);
      setOpen(false);
      onDeleted();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button onClick={openIt} variant="ghost" className="h-8 px-2 text-xs text-flag-red hover:bg-red-50 font-semibold" data-testid={`admin-user-delete-${user.id}`}>
          <Trash2 className="h-3 w-3 mr-1" /> Delete
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent data-testid={`admin-delete-confirm-${user.id}`}>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete {user.email}?</AlertDialogTitle>
          <AlertDialogDescription>This permanently removes the account and every record below. It cannot be undone.</AlertDialogDescription>
        </AlertDialogHeader>
        {preview ? (
          <div className="text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-lg p-3 max-h-60 overflow-y-auto">
            <ul className="space-y-1">
              {Object.entries(preview.counts).filter(([, n]) => n > 0).map(([k, n]) => (
                <li key={k} className="flex justify-between"><span className="text-slate-600">{k}</span><strong>{n}</strong></li>
              ))}
              {Object.values(preview.counts).every((n) => !n) && <li className="text-slate-400 italic">No records to remove.</li>}
            </ul>
          </div>
        ) : <div className="text-slate-500 text-sm">Inventory…</div>}
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={confirmDelete} className="bg-flag-red hover:bg-flag-red-hover">Permanently delete</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function CreateUserDialog({ open, setOpen, onCreated }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("admin");
  const [accountType, setAccountType] = useState("candidate");
  const [status, setStatus] = useState("free");
  const [trialDays, setTrialDays] = useState(14);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [tempPw, setTempPw] = useState("");

  const submit = async () => {
    if (!name.trim() || !email.trim()) return toast.error("Name and email required");
    setBusy(true);
    try {
      const body = { email: email.trim(), name: name.trim(), account_type: accountType, status, role };
      if (status === "trial") body.trial_days = trialDays;
      if (notes.trim()) body.comp_notes = notes.trim();
      const { data } = await api.post("/admin/users", body);
      setTempPw(data.temp_password);
      onCreated();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const reset = () => { setName(""); setEmail(""); setRole("admin"); setAccountType("candidate"); setStatus("free"); setTrialDays(14); setNotes(""); setTempPw(""); };

  return (
    <AlertDialog open={open} onOpenChange={(o) => { if (!o) reset(); setOpen(o); }}>
      <AlertDialogContent data-testid="admin-create-user-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>{tempPw ? "Account created" : "Create account"}</AlertDialogTitle>
          <AlertDialogDescription>{tempPw ? "Share these credentials with the user. They'll be asked to change the password on first login." : "A secure temp password is generated automatically and copied to your clipboard."}</AlertDialogDescription>
        </AlertDialogHeader>
        {!tempPw ? (
          <div className="space-y-3">
            <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1 h-10" data-testid="admin-create-name" /></div>
            <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Email</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 h-10" data-testid="admin-create-email" /></div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Role</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger className="mt-1 h-10" data-testid="admin-create-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin — full Electable access</SelectItem>
                  <SelectItem value="caller">Caller — locked to /caller only</SelectItem>
                </SelectContent>
              </Select>
              {role === "caller" && (
                <p className="text-[11px] text-slate-500 mt-1 leading-snug">
                  Caller accounts see only the donor call list. Import their donor CSV and edit the Donor Message from the <strong>Callers</strong> tab.
                </p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Account type</Label>
                <Select value={accountType} onValueChange={setAccountType}><SelectTrigger className="mt-1 h-10" data-testid="admin-create-type"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="candidate">Candidate</SelectItem><SelectItem value="committee">Committee</SelectItem></SelectContent></Select>
              </div>
              <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Status</Label>
                <Select value={status} onValueChange={setStatus}><SelectTrigger className="mt-1 h-10" data-testid="admin-create-status"><SelectValue /></SelectTrigger><SelectContent>{["free","trial","comp","paid"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
              </div>
            </div>
            {status === "trial" && (<div><Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Trial days</Label><Input type="number" min={1} max={365} value={trialDays} onChange={(e) => setTrialDays(Number(e.target.value))} className="mt-1 h-10" data-testid="admin-create-trial-days" /></div>)}
            {status === "comp" && (<div><Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Comp notes</Label><Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="admin-create-notes" /></div>)}
          </div>
        ) : (
          <div className="space-y-3">
            <div className="text-sm"><strong>{email}</strong></div>
            <div className="flex items-center gap-2 bg-slate-50 border-2 border-flag-red rounded-lg px-3 py-2">
              <code className="font-mono text-flag-navy flex-1" data-testid="admin-create-temp-pw">{tempPw}</code>
              <Button onClick={() => copyToClipboard(tempPw)} variant="outline" className="h-8 border-2 font-semibold"><Copy className="h-3 w-3 mr-1" /> Copy</Button>
            </div>
            <p className="text-xs text-slate-500">Tell the user to log in with this password and change it from Settings → Change Password.</p>
          </div>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="admin-create-cancel">{tempPw ? "Done" : "Cancel"}</AlertDialogCancel>
          {!tempPw && <AlertDialogAction onClick={submit} disabled={busy} className="bg-flag-red hover:bg-flag-red-hover" data-testid="admin-create-submit">{busy ? "Creating…" : "Create"}</AlertDialogAction>}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function UserUsageDrawer({ user }) {
  const [stats, setStats] = useState(null);
  const [err, setErr] = useState("");

  const load = () => {
    setErr("");
    setStats(null);
    api.get(bust(`/admin/analytics/users/${user.id}`))
      .then(({ data }) => setStats(data))
      .catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  };
  // Refetch when the drawer targets a different user. `load` is recreated
  // every render (it only reads user.id), so depending on user.id directly
  // is the precise trigger.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [user.id]);

  // Bucket feature counts into the 5 user-facing tool categories.
  const groupCounts = useMemo(() => {
    const byFeature = {};
    const lastByFeature = {};
    for (const f of stats?.features || []) {
      byFeature[f.feature] = f.count;
      lastByFeature[f.feature] = f.last_used;
    }
    return FEATURE_GROUPS.map((g) => {
      let total = 0;
      let last = null;
      const details = [];
      for (const feat of g.features) {
        const c = byFeature[feat] || 0;
        total += c;
        if (c > 0) details.push({ feature: feat, count: c, last_used: lastByFeature[feat] });
        const lu = lastByFeature[feat];
        if (lu && (!last || lu > last)) last = lu;
      }
      return { ...g, total, last, details };
    });
  }, [stats]);

  if (err) {
    return (
      <div className="text-sm text-red-700" data-testid={`admin-user-usage-error-${user.id}`}>
        Couldn&rsquo;t load usage: {err}
        <Button onClick={load} variant="outline" className="ml-2 h-7 px-2 text-xs border-2 font-semibold">Retry</Button>
      </div>
    );
  }
  if (!stats) {
    return <div className="text-slate-500 text-sm" data-testid={`admin-user-usage-loading-${user.id}`}>Loading usage…</div>;
  }

  return (
    <div className="space-y-4" data-testid={`admin-user-usage-${user.id}`}>
      <div className="flex items-center justify-between">
        <div>
          <div className="font-display text-base font-bold text-flag-navy">{user.name || user.email} · Usage</div>
          <div className="text-xs text-slate-500">Account metadata only — no post content, no voter data, no messages.</div>
        </div>
        <Button onClick={load} variant="outline" className="h-8 px-2 text-xs border-2 font-semibold" data-testid={`admin-user-usage-refresh-${user.id}`}>
          <RefreshCw className="h-3 w-3 mr-1" /> Refresh
        </Button>
      </div>

      {/* Top-line engagement stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <MetricCard icon={Activity} label="Total logins" value={stats.login_total} testId={`usage-logins-${user.id}`} />
        <MetricCard icon={Clock}    label="Last login"  value={fmtDate(stats.last_login_at)} testId={`usage-last-login-${user.id}`} />
        <MetricCard icon={CalendarDays} label="Active days" value={stats.active_days_total ?? 0} testId={`usage-active-days-${user.id}`} />
        <MetricCard icon={Timer} label="Time engaged" value={stats.engagement?.total_human || "0m"} testId={`usage-total-time-${user.id}`} />
        <MetricCard icon={Timer} label="Avg / session" value={stats.engagement?.avg_session_human || "0m"} testId={`usage-avg-session-${user.id}`} />
        <MetricCard icon={BarChart3} label="Sessions" value={stats.engagement?.session_count ?? 0} testId={`usage-session-count-${user.id}`} />
      </div>

      {/* Feature category usage */}
      <div>
        <h4 className="font-display text-sm font-bold text-flag-navy uppercase tracking-wider">Feature usage</h4>
        <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3" data-testid={`usage-feature-groups-${user.id}`}>
          {groupCounts.map((g) => (
            <div
              key={g.key}
              className="bg-white border border-slate-200 rounded-lg p-3"
              data-testid={`usage-group-${g.key}-${user.id}`}
            >
              <div className="flex items-center gap-2 text-slate-600">
                <g.icon className="h-4 w-4 text-flag-red" />
                <span className="text-xs font-bold uppercase tracking-wider">{g.label}</span>
              </div>
              <div className="mt-1 font-display text-2xl font-black text-flag-navy" data-testid={`usage-group-${g.key}-count-${user.id}`}>
                {g.total.toLocaleString()}
              </div>
              <div className="text-[11px] text-slate-500">
                {g.last ? <>last used {fmtDate(g.last)}</> : "no activity yet"}
              </div>
              {g.details.length > 0 && (
                <ul className="mt-2 space-y-0.5 border-t border-slate-100 pt-2">
                  {g.details.map((d) => (
                    <li key={d.feature} className="flex items-center justify-between text-[11px] text-slate-600">
                      <span>{FEATURE_LABELS[d.feature] || d.feature}</span>
                      <strong className="text-flag-navy">{d.count}</strong>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Daily login chart (existing) */}
      {stats.login_days && stats.login_days.length > 0 && (
        <div>
          <h4 className="font-display text-sm font-bold text-flag-navy uppercase tracking-wider">Logins by day (90d)</h4>
          <div className="mt-2 flex items-end gap-1 h-16" data-testid={`usage-login-chart-${user.id}`}>
            {stats.login_days.map(([day, n]) => (
              <div key={day} className="flex-1 bg-flag-navy/80 rounded-t" style={{ height: `${Math.min(100, n * 20)}%` }} title={`${day}: ${n}`} />
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between text-xs text-slate-500">
        <span>Setup complete: <strong className="text-flag-navy">{stats.setup_complete_pct}%</strong></span>
        <span className="italic">Privacy: metadata only. Time is measured from heartbeats and closed after 5 min of inactivity.</span>
      </div>
    </div>
  );
}

function MetricCard({ icon: Icon, label, value, testId }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg px-3 py-2" data-testid={testId}>
      <div className="flex items-center gap-1.5 text-slate-500">
        <Icon className="h-3 w-3" />
        <span className="text-[10px] font-bold uppercase tracking-wider">{label}</span>
      </div>
      <div className="font-display text-base font-black text-flag-navy truncate" title={String(value)}>{value}</div>
    </div>
  );
}

function Stat({ label, value }) {
  return <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2"><div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</div><div className="font-display text-lg font-black text-flag-navy">{value}</div></div>;
}

/* ---------------- Analytics ---------------- */
function AnalyticsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastLoadedAt, setLastLoadedAt] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(bust("/admin/analytics/aggregate"));
      setData(data);
      setLastLoadedAt(new Date());
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!autoRefresh) return undefined;
    const id = setInterval(load, 30_000);
    return () => clearInterval(id);
  }, [autoRefresh]);

  const featureMax = useMemo(
    () => Math.max(1, ...(data?.feature_counts || []).map((f) => f.count)),
    [data],
  );
  // feature_counts is desc by count. Take the bottom 5 (ascending) for the
  // least-used panel — useful for spotting features no one's touching.
  const leastUsed = useMemo(
    () => [...(data?.feature_counts || [])].sort((a, b) => a.count - b.count).slice(0, 5),
    [data],
  );

  return (
    <div data-testid="admin-analytics">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div className="flex items-center gap-2">
          <Button onClick={load} variant="outline" className="border-2 font-semibold h-9" data-testid="admin-analytics-refresh-btn" disabled={loading} title="Re-pull live from the database">
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <label className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 cursor-pointer select-none">
            <input type="checkbox" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} data-testid="admin-analytics-autorefresh" />
            Auto-refresh 30s
          </label>
        </div>
        <div className="text-xs text-slate-500" data-testid="admin-analytics-meta">
          {lastLoadedAt && <>live from database at <span className="font-semibold text-slate-700">{lastLoadedAt.toLocaleTimeString()}</span></>}
        </div>
      </div>

      {!data ? <div className="text-slate-500">Loading…</div> : (
      <div className="grid lg:grid-cols-2 gap-6">
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5">
        <h3 className="font-display text-lg font-bold text-flag-navy">Overview</h3>
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-3">
          <Stat label="Total accounts" value={data.total_users.toLocaleString()} />
          <Stat label="New 7d" value={(data.signups_7d ?? 0).toLocaleString()} />
          <Stat label="New 30d" value={(data.signups_30d ?? 0).toLocaleString()} />
          <Stat label="Active 7d" value={data.active_7d.toLocaleString()} />
          <Stat label="Active 30d" value={data.active_30d.toLocaleString()} />
          <Stat label="Paid" value={data.status_breakdown.paid.toLocaleString()} />
          <Stat label="Candidates" value={data.account_type_breakdown.candidate.toLocaleString()} />
          <Stat label="Committees" value={data.account_type_breakdown.committee.toLocaleString()} />
          <Stat label="Staffers" value={(data.account_type_breakdown.staffer ?? 0).toLocaleString()} />
        </div>
        <h4 className="font-display text-sm font-bold text-flag-navy uppercase tracking-wider mt-5">Status breakdown</h4>
        <ul className="mt-2 space-y-1.5">
          {Object.entries(data.status_breakdown).map(([s, n]) => (
            <li key={s} className="flex items-center justify-between text-sm"><span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_COLORS[s] || "bg-slate-100"}`}>{s}</span><strong className="text-flag-navy">{n}</strong></li>
          ))}
        </ul>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5">
        <h3 className="font-display text-lg font-bold text-flag-navy">Most-used features (90d)</h3>
        {data.feature_counts.length === 0 ? <p className="text-sm text-slate-500 mt-2">No usage recorded yet.</p> : (
          <ul className="mt-3 space-y-2" data-testid="admin-analytics-features-most">
            {data.feature_counts.map((f) => (
              <li key={f.feature} className="text-sm">
                <div className="flex items-center justify-between"><span className="text-slate-700">{FEATURE_LABELS[f.feature] || f.feature}</span><strong className="text-flag-navy">{f.count}</strong></div>
                <div className="mt-1 h-1.5 bg-slate-100 rounded-full"><div className="h-full bg-flag-red rounded-full" style={{ width: `${(f.count / featureMax) * 100}%` }} /></div>
              </li>
            ))}
          </ul>
        )}
        {leastUsed.length > 0 && (
          <>
            <h4 className="font-display text-sm font-bold text-flag-navy uppercase tracking-wider mt-5">Least-used features</h4>
            <ul className="mt-2 space-y-1" data-testid="admin-analytics-features-least">
              {leastUsed.map((f) => (
                <li key={`l-${f.feature}`} className="flex items-center justify-between text-sm text-slate-600 border-b border-slate-100 pb-1">
                  <span>{FEATURE_LABELS[f.feature] || f.feature}</span>
                  <strong className="text-flag-navy">{f.count}</strong>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
      {data.signups_series.length > 0 && (
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl shadow-sm p-5">
          <h3 className="font-display text-lg font-bold text-flag-navy">Signups (last 30d)</h3>
          <div className="mt-3 flex items-end gap-1 h-32">
            {data.signups_series.map(([day, n]) => (
              <div key={day} className="flex-1 bg-flag-navy rounded-t" style={{ height: `${Math.min(100, n * 25)}%` }} title={`${day}: ${n}`} />
            ))}
          </div>
        </div>
      )}
      <p className="lg:col-span-2 text-xs text-slate-500 italic" data-testid="admin-privacy-note">Privacy: aggregate metadata only &mdash; no user content is surfaced in this console.</p>
      </div>
      )}
    </div>
  );
}

/* ---------------- Codes ---------------- */
function CodesTab() {
  const [rows, setRows] = useState([]);
  const [codeType, setCodeType] = useState("trial");
  const [trialDays, setTrialDays] = useState(14);
  const [label, setLabel] = useState("");

  const load = async () => {
    try { const { data } = await api.get("/admin/codes"); setRows(data || []); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      const body = { code_type: codeType, label };
      if (codeType === "trial") body.trial_days = trialDays;
      const { data } = await api.post("/admin/codes", body);
      copyToClipboard(data.code);
      toast.success(`Created ${data.code} (copied)`);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const deactivate = async (id) => {
    try { await api.patch(`/admin/codes/${id}`); toast.success("Deactivated"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  return (
    <div className="space-y-4" data-testid="admin-codes">
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5">
        <h3 className="font-display text-lg font-bold text-flag-navy">Generate code</h3>
        <div className="mt-3 grid sm:grid-cols-4 gap-3">
          <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Type</Label>
            <Select value={codeType} onValueChange={setCodeType}><SelectTrigger className="mt-1 h-10" data-testid="admin-code-type"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="trial">Trial</SelectItem><SelectItem value="comp">Comp</SelectItem></SelectContent></Select>
          </div>
          {codeType === "trial" && (<div><Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Trial days</Label><Input type="number" min={1} max={365} value={trialDays} onChange={(e) => setTrialDays(Number(e.target.value))} className="mt-1 h-10" data-testid="admin-code-days" /></div>)}
          <div className="sm:col-span-2"><Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Label (internal)</Label><Input value={label} onChange={(e) => setLabel(e.target.value)} className="mt-1 h-10" placeholder="e.g. Pilot Ontario Co." data-testid="admin-code-label" /></div>
        </div>
        <Button onClick={create} className="mt-3 bg-flag-red hover:bg-flag-red-hover text-white font-bold h-10" data-testid="admin-code-create-btn">Generate</Button>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
            <tr><th className="text-left px-4 py-3 font-bold">Code</th><th className="text-left px-3 py-3 font-bold">Type</th><th className="text-left px-3 py-3 font-bold">Trial days</th><th className="text-left px-3 py-3 font-bold">Label</th><th className="text-left px-3 py-3 font-bold">Status</th><th className="text-left px-3 py-3 font-bold">Created</th><th className="text-right px-3 py-3 font-bold">Actions</th></tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.id} className="border-t border-slate-100" data-testid={`admin-code-row-${c.id}`}>
                <td className="px-4 py-3"><code className="font-mono text-flag-navy">{c.code}</code></td>
                <td className="px-3 py-3 text-slate-700">{c.code_type}</td>
                <td className="px-3 py-3 text-slate-700">{c.trial_days ?? "—"}</td>
                <td className="px-3 py-3 text-slate-700">{c.label || "—"}</td>
                <td className="px-3 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${c.status === "active" ? "bg-green-100 text-green-900" : c.status === "used" ? "bg-slate-200 text-slate-700" : "bg-slate-100 text-slate-500"}`}>{c.status}</span></td>
                <td className="px-3 py-3 text-slate-700 whitespace-nowrap">{fmtDate(c.created_at)}</td>
                <td className="px-3 py-3 text-right whitespace-nowrap">
                  <Button onClick={() => copyToClipboard(c.code)} variant="outline" className="h-8 px-2 text-xs border-2 font-semibold mr-1" data-testid={`admin-code-copy-${c.id}`}><Copy className="h-3 w-3 mr-1" />Copy</Button>
                  {c.status === "active" && <Button onClick={() => deactivate(c.id)} variant="ghost" className="h-8 px-2 text-xs text-flag-red hover:bg-red-50 font-semibold" data-testid={`admin-code-deactivate-${c.id}`}>Deactivate</Button>}
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-slate-500">No codes yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---------------- Invites ---------------- */
function InvitesTab() {
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [rows, setRows] = useState([]);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/invites");
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };
  useEffect(() => { load(); }, []);

  const onSend = async (e) => {
    e.preventDefault();
    const addr = email.trim().toLowerCase();
    if (!addr) return;
    setBusy(true);
    try {
      const origin = typeof window !== "undefined" ? window.location.origin : "";
      const { data } = await api.post("/admin/invites", {
        email: addr,
        note: note.trim() || null,
        redirect_origin: origin,
      });
      toast.success(`Invite sent to ${addr}`);
      setEmail("");
      setNote("");
      // Optimistic refresh
      load();
      // Surface the signup link for the admin's records
      if (data?.signup_link) console.debug("invite signup_link:", data.signup_link);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-invites">
      <p className="text-sm text-slate-600">
        Send a branded signup email from <code className="bg-slate-100 px-1.5 py-0.5 rounded text-xs">{`hello@electable-ai.com`}</code>. The invite link is single-use, expires in 7 days, and auto-fills the recipient&rsquo;s email on the signup form.
      </p>

      <form onSubmit={onSend} className="bg-white border border-slate-200 rounded-xl p-5 grid sm:grid-cols-2 gap-3" data-testid="admin-invite-form">
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Recipient email</Label>
          <Input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="person@example.com"
            data-testid="admin-invite-email"
            className="mt-1 h-11"
          />
        </div>
        <div className="sm:row-span-2">
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Personal note (optional)</Label>
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Hey, thought you'd want to try this for your race…"
            rows={3}
            maxLength={400}
            data-testid="admin-invite-note"
            className="mt-1"
          />
        </div>
        <div>
          <Button type="submit" disabled={busy} data-testid="admin-invite-send" className="bg-flag-red hover:bg-flag-red-hover text-white font-bold h-11 px-5">
            <Send className="h-4 w-4 mr-1.5" /> {busy ? "Sending…" : "Send invite"}
          </Button>
        </div>
      </form>

      <div className="flex items-center justify-between">
        <h3 className="font-display font-bold text-flag-navy">Recent invites</h3>
        <Button onClick={load} variant="outline" className="h-9 border-2 font-semibold" data-testid="admin-invites-refresh">
          <RefreshCw className="h-3 w-3 mr-1" /> Refresh
        </Button>
      </div>

      {rows.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-xl p-8 text-center text-slate-500 text-sm" data-testid="admin-invites-empty">
          No invites sent yet.
        </div>
      ) : (
        <ul className="space-y-2">
          {rows.map((r) => {
            const accepted = !!r.accepted_at;
            const expired = !accepted && r.expires_at && new Date(r.expires_at) < new Date();
            return (
              <li
                key={`${r.email}-${r.created_at}`}
                className="bg-white border border-slate-200 rounded-xl shadow-sm p-4"
                data-testid={`admin-invite-row-${r.email}`}
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <div className="font-semibold text-flag-navy">{r.email}</div>
                    <div className="text-xs text-slate-500">
                      Sent {fmtDate(r.created_at)}
                      {r.email_sent_id ? " · delivered ✅" : r.email_error ? " · delivery failed" : ""}
                      {" · "}Expires {fmtDate(r.expires_at)}
                    </div>
                  </div>
                  <div className="text-xs font-bold uppercase tracking-wider">
                    {accepted ? (
                      <span className="inline-flex items-center gap-1 text-green-700"><Check className="h-3 w-3" /> Accepted</span>
                    ) : expired ? (
                      <span className="text-slate-500">Expired</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-amber-700"><Clock className="h-3 w-3" /> Pending</span>
                    )}
                  </div>
                </div>
                {r.note && (
                  <div className="mt-2 text-xs text-slate-600 italic border-l-2 border-flag-red/30 pl-2">{r.note}</div>
                )}
                {r.email_error && (
                  <div className="mt-2 text-xs text-red-700 font-mono break-all">{r.email_error}</div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/* ---------------- Callers ---------------- */
function CallersTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(bust("/admin/callers"));
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-4" data-testid="admin-callers">
      <div className="bg-flag-red/5 border border-flag-red/20 rounded-xl px-4 py-3 text-sm text-slate-700">
        <strong className="text-flag-red">Caller Mode</strong> — locked-down single-screen donor call tool for the candidate&rsquo;s phone. Create a caller account under the <em>Accounts</em> tab (set Role = Caller), then import their donor CSV and edit the Donor Message below. Their donor data lives in an isolated store (<code className="bg-slate-100 px-1 rounded text-xs">caller_donors</code>) and never appears in any other Electable view.
      </div>
      <MessageAuditPanel />
      <DemoAccountPanel />
      <div className="flex items-center justify-between">
        <div className="text-xs text-slate-500">{rows.length} caller account{rows.length === 1 ? "" : "s"}</div>
        <Button onClick={load} variant="outline" className="h-9 border-2 font-semibold" data-testid="admin-callers-refresh">
          <RefreshCw className={`h-3 w-3 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>
      {rows.length === 0 && !loading && (
        <div className="bg-white border border-dashed border-slate-300 rounded-xl p-8 text-center text-slate-500 text-sm" data-testid="admin-callers-empty">
          No caller accounts yet. Create one from the Accounts tab and set <strong>Role = Caller</strong>.
        </div>
      )}
      <div className="space-y-4">
        {rows.map((c) => (<CallerCard key={c.id} caller={c} onChanged={load} />))}
      </div>
    </div>
  );
}

function HeaderMapReport({ result, testid }) {
  const [open, setOpen] = useState(false);
  if (!result) return null;
  const mapped = result.header_map || {};
  const unmapped = result.unmapped_headers || [];
  const mappedEntries = Object.entries(mapped);
  if (mappedEntries.length === 0 && unmapped.length === 0) return null;
  return (
    <div className="mt-2" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="text-[11px] font-bold uppercase tracking-wider text-flag-navy hover:underline"
        data-testid={`${testid}-toggle`}
      >
        {open ? "Hide" : "Show"} column mapping ({mappedEntries.length} matched
        {unmapped.length > 0 ? `, ${unmapped.length} unmapped` : ""})
      </button>
      {open && (
        <div className="mt-2 grid gap-2 sm:grid-cols-2">
          <div className="bg-slate-50 border border-slate-200 rounded-md p-2">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Mapped</div>
            {mappedEntries.length === 0 ? (
              <div className="text-[11px] text-slate-400 italic">none</div>
            ) : (
              <ul className="text-[11px] text-slate-700 space-y-0.5 max-h-40 overflow-auto">
                {mappedEntries.map(([raw, canon]) => (
                  <li key={raw} className="font-mono">
                    <span className="text-slate-500">{raw}</span>
                    <span className="text-slate-400 px-1">→</span>
                    <span className="text-flag-navy font-bold">{canon}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-md p-2">
            <div className="text-[10px] font-bold uppercase tracking-wider text-amber-800 mb-1">Unmapped (dropped)</div>
            {unmapped.length === 0 ? (
              <div className="text-[11px] text-slate-400 italic">none — every column matched</div>
            ) : (
              <ul className="text-[11px] text-amber-900 space-y-0.5 max-h-40 overflow-auto">
                {unmapped.map((h) => (
                  <li key={h} className="font-mono">{h}</li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function CategoriesPanel({ callerId, refreshToken = 0, onImported = null }) {
  const [cats, setCats] = useState(null);
  const [busy, setBusy] = useState(false);
  const [newName, setNewName] = useState("");
  const [newType, setNewType] = useState("call_list");
  const [uploadingId, setUploadingId] = useState(null);
  const [openMessagesId, setOpenMessagesId] = useState(null);
  const fileInputs = useRef({});

  const load = async () => {
    try {
      const { data } = await api.get(bust(`/admin/callers/${callerId}/categories`));
      setCats(data.categories || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  useEffect(() => { load(); }, [callerId, refreshToken]);  // eslint-disable-line react-hooks/exhaustive-deps

  const createCategory = async () => {
    const name = newName.trim();
    if (!name || busy) return;
    setBusy(true);
    try {
      await api.post(`/admin/callers/${callerId}/categories`, {
        name,
        category_type: newType,
      });
      setNewName("");
      setNewType("call_list");
      await load();
      toast.success(`Category "${name}" created`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const setCategoryType = async (cat, category_type) => {
    if (cat.category_type === category_type) return;
    try {
      await api.patch(`/admin/callers/${callerId}/categories/${cat.id}`, { category_type });
      await load();
      const labelMap = {
        prospecting_list: "Prospecting List",
        pac_list: "PAC List",
        call_list: "Call List",
      };
      toast.success(`"${cat.name}" is now a ${labelMap[category_type] || "Call List"}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const renameCategory = async (cat) => {
    const name = window.prompt("New name for this category:", cat.name);  // eslint-disable-line no-alert
    if (!name || name.trim() === cat.name) return;
    try {
      await api.patch(`/admin/callers/${callerId}/categories/${cat.id}`, { name: name.trim() });
      await load();
      toast.success("Renamed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const deleteCategory = async (cat) => {
    if (!window.confirm(  // eslint-disable-line no-alert
      `Delete this list?\n\nThe donors stay in your account and on their other lists — only "${cat.name}" is removed.\n\n${cat.count} donor${cat.count === 1 ? "" : "s"} on this list will keep their canonical record, giving history, notes, and every other list membership.`
    )) return;
    try {
      const { data } = await api.delete(`/admin/callers/${callerId}/categories/${cat.id}`);
      await load();
      toast.success(`List deleted (${data.removed_memberships ?? 0} membership(s) removed — donors kept)`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const uploadToCategory = async (cat, file) => {
    if (!file || uploadingId) return;
    setUploadingId(cat.id);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(
        `/admin/callers/${callerId}/donors/import?category_id=${cat.id}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      const parts = [`${(data.created || 0).toLocaleString()} new`, `${(data.updated || 0).toLocaleString()} updated`];
      if (data.updated_contact_match > 0) parts.push(`${data.updated_contact_match.toLocaleString()} matched by contact info`);
      if (data.needs_review > 0) parts.push(`${data.needs_review.toLocaleString()} need review`);
      if (data.moved_to_maxed > 0) parts.push(`${data.moved_to_maxed.toLocaleString()} moved to Maxed Donors`);
      if (data.created_maxed > 0) parts.push(`${data.created_maxed.toLocaleString()} added to Maxed Donors`);
      toast.success(`"${cat.name}": ${parts.join(" · ")}`);
      await load();
      // Refresh the Stage-2 review panel + summary counts in the parent.
      onImported && onImported();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setUploadingId(null);
      if (fileInputs.current[cat.id]) fileInputs.current[cat.id].value = "";
    }
  };

  return (
    <div className="mt-4 border-t border-slate-100 pt-4" data-testid={`admin-caller-categories-${callerId}`}>
      <div className="text-xs font-bold uppercase tracking-wider text-slate-600">Categories</div>
      <p className="text-[11px] text-slate-500 mt-1 leading-snug">
        Named donor lists this caller sees as tabs. Empty categories don&rsquo;t render. Uploads match existing donors (first + last name, city, state), update their giving, and add new ones &mdash; nothing is deleted. Donors who hit the $7,000 cycle max move to Maxed Donors automatically. Column mapping is identical to the House File importer below.
      </p>

      {cats === null ? (
        <p className="mt-2 text-xs text-slate-400 italic">Loading…</p>
      ) : cats.length === 0 ? (
        <p className="mt-2 text-xs text-slate-500 italic">No categories yet. Create one below.</p>
      ) : (
        <ul className="mt-2 divide-y divide-slate-100 border border-slate-200 rounded-lg" data-testid={`admin-caller-categories-list-${callerId}`}>
          {cats.map((c) => {
            const catType = c.category_type || "call_list";
            const isProspecting = catType === "prospecting_list";
            const isPac = catType === "pac_list";
            const isMaxed = catType === "maxed";
            const messagesOpen = openMessagesId === c.id;
            return (
              <li key={c.id} className="p-2.5 space-y-2" data-testid={`admin-caller-category-${c.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <div className="font-semibold text-flag-navy text-sm truncate">{c.name}</div>
                      <span
                        className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${
                          isMaxed
                            ? "bg-flag-red text-white"
                            : isProspecting
                            ? "bg-amber-100 text-amber-900"
                            : isPac
                            ? "bg-emerald-100 text-emerald-900"
                            : "bg-slate-100 text-slate-700"
                        }`}
                        data-testid={`admin-caller-category-type-badge-${c.id}`}
                      >
                        {isMaxed ? "Maxed" : isProspecting ? "Prospecting List" : isPac ? "PAC List" : "Call List"}
                      </span>
                    </div>
                    <div className="text-[10.5px] text-slate-500 mt-0.5">
                      {c.count.toLocaleString()} donor{c.count === 1 ? "" : "s"}
                      {c.list_type_legacy && <span className="ml-1 text-slate-400">· migrated {c.list_type_legacy}</span>}
                    </div>
                  </div>
                  <input
                    type="file"
                    accept=".csv,text/csv"
                    ref={(el) => { fileInputs.current[c.id] = el; }}
                    onChange={(e) => uploadToCategory(c, e.target.files?.[0])}
                    className="hidden"
                    data-testid={`admin-caller-category-file-${c.id}`}
                  />
                  {!isMaxed && (
                    <button
                      type="button"
                      onClick={() => fileInputs.current[c.id]?.click()}
                      disabled={uploadingId === c.id}
                      className="text-[11px] font-bold uppercase tracking-wider text-flag-navy hover:underline disabled:opacity-40"
                      data-testid={`admin-caller-category-upload-${c.id}`}
                    >
                      {uploadingId === c.id ? "Uploading…" : "Upload"}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setOpenMessagesId(messagesOpen ? null : c.id)}
                    className="text-[11px] font-bold uppercase tracking-wider text-flag-navy hover:underline"
                    data-testid={`admin-caller-category-messages-toggle-${c.id}`}
                    aria-expanded={messagesOpen}
                  >
                    {messagesOpen ? "Close messages" : "Edit messages"}
                  </button>
                  <button
                    type="button"
                    onClick={() => renameCategory(c)}
                    className="text-[11px] font-bold uppercase tracking-wider text-slate-600 hover:underline"
                    data-testid={`admin-caller-category-rename-${c.id}`}
                  >
                    Rename
                  </button>
                  <button
                    type="button"
                    onClick={() => deleteCategory(c)}
                    className="text-[11px] font-bold uppercase tracking-wider text-flag-red hover:underline"
                    data-testid={`admin-caller-category-delete-${c.id}`}
                  >
                    Delete
                  </button>
                </div>
                {!isMaxed && (
                <div className="flex items-center gap-2 pl-1">
                  <span className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold">Type:</span>
                  <div className="inline-flex rounded-md overflow-hidden border border-slate-300" role="radiogroup" aria-label="Category type">
                    <button
                      type="button"
                      role="radio"
                      aria-checked={catType === "call_list"}
                      onClick={() => setCategoryType(c, "call_list")}
                      className={`px-2.5 py-1 text-[11px] font-bold ${
                        catType === "call_list"
                          ? "bg-flag-navy text-white"
                          : "bg-white text-slate-700 hover:bg-slate-50"
                      }`}
                      data-testid={`admin-caller-category-type-call-${c.id}`}
                    >
                      Call List
                    </button>
                    <button
                      type="button"
                      role="radio"
                      aria-checked={catType === "pac_list"}
                      onClick={() => setCategoryType(c, "pac_list")}
                      className={`px-2.5 py-1 text-[11px] font-bold border-l border-slate-300 ${
                        catType === "pac_list"
                          ? "bg-emerald-600 text-white"
                          : "bg-white text-slate-700 hover:bg-slate-50"
                      }`}
                      data-testid={`admin-caller-category-type-pac-${c.id}`}
                    >
                      PAC List
                    </button>
                    <button
                      type="button"
                      role="radio"
                      aria-checked={catType === "prospecting_list"}
                      onClick={() => setCategoryType(c, "prospecting_list")}
                      className={`px-2.5 py-1 text-[11px] font-bold border-l border-slate-300 ${
                        catType === "prospecting_list"
                          ? "bg-amber-500 text-white"
                          : "bg-white text-slate-700 hover:bg-slate-50"
                      }`}
                      data-testid={`admin-caller-category-type-prospecting-${c.id}`}
                    >
                      Prospecting List
                    </button>
                  </div>
                </div>
                )}
                {messagesOpen && (
                  <div className="mt-3 border border-slate-200 rounded-lg p-3 bg-slate-50" data-testid={`admin-caller-category-messages-${c.id}`}>
                    <MessageEditor
                      scope="owner"
                      callerId={callerId}
                      categoryId={c.id}
                      categoryType={c.category_type || "call_list"}
                      categoryName={c.name}
                      testIdPrefix={`admin-msg-${c.id}`}
                    />
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <div className="mt-3 border border-slate-200 rounded-lg p-3 bg-slate-50 space-y-2">
        <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold">Add a new category</div>
        <div className="flex flex-wrap items-center gap-2">
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); createCategory(); } }}
            placeholder="Category name (e.g. Major Donors)"
            className="h-9 text-sm max-w-xs"
            data-testid={`admin-caller-category-new-name-${callerId}`}
          />
          <div className="inline-flex rounded-md overflow-hidden border border-slate-300" role="radiogroup" aria-label="New category type">
            <button
              type="button"
              role="radio"
              aria-checked={newType === "call_list"}
              onClick={() => setNewType("call_list")}
              className={`px-2.5 py-1.5 text-[11px] font-bold ${
                newType === "call_list"
                  ? "bg-flag-navy text-white"
                  : "bg-white text-slate-700 hover:bg-slate-50"
              }`}
              data-testid={`admin-caller-category-new-type-call-${callerId}`}
            >
              Call List
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={newType === "pac_list"}
              onClick={() => setNewType("pac_list")}
              className={`px-2.5 py-1.5 text-[11px] font-bold border-l border-slate-300 ${
                newType === "pac_list"
                  ? "bg-emerald-600 text-white"
                  : "bg-white text-slate-700 hover:bg-slate-50"
              }`}
              data-testid={`admin-caller-category-new-type-pac-${callerId}`}
            >
              PAC List
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={newType === "prospecting_list"}
              onClick={() => setNewType("prospecting_list")}
              className={`px-2.5 py-1.5 text-[11px] font-bold border-l border-slate-300 ${
                newType === "prospecting_list"
                  ? "bg-amber-500 text-white"
                  : "bg-white text-slate-700 hover:bg-slate-50"
              }`}
              data-testid={`admin-caller-category-new-type-prospecting-${callerId}`}
            >
              Prospecting List
            </button>
          </div>
          <Button
            onClick={createCategory}
            disabled={busy || !newName.trim()}
            className="h-9 bg-flag-navy hover:bg-flag-navy/90 text-white font-bold text-xs"
            data-testid={`admin-caller-category-create-${callerId}`}
          >
            Add category
          </Button>
        </div>
        <p className="text-[10.5px] text-slate-500 leading-snug">
          <strong>Call List</strong> — donors with giving history. Shows capacity, ask buttons, and giving history. Subject to the $7,000 cycle cap.
          <br />
          <strong>PAC List</strong> — donors giving to a PAC / non-candidate committee. No cycle cap: no MAXED badge, no capacity box; giving history still shows. Asks are based on highest gift or a manual amount.
          <br />
          <strong>Prospecting List</strong> — donors with no giving history. Hides capacity, ask buttons, and giving history; shows town/state on the card.
        </p>
      </div>
    </div>
  );
}

/**
 * Per-caller campaign config panel (Federal / State — Matching Funds).
 *
 * Owner-only editor rendered inside `CallerCard`. This is the ONLY place
 * in the whole product that surfaces `campaign_type` and the matching-
 * funds config — self-serve subscription users never see it. Values are
 * loaded from and saved to the caller-user document via the owner-gated
 * /api/admin/callers/{user_id}/campaign-config endpoints.
 *
 * A caller with no config yet reads back cleanly as Federal / $250 /
 * Primary because the server applies defaults on read.
 */
function ConsolidationCard({ callerId, onChanged = null }) {
  const [preview, setPreview] = useState(null);   // last dry-run report
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [applyOpen, setApplyOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [backupOn, setBackupOn] = useState(true);
  const [applying, setApplying] = useState(false);
  const [lastApply, setLastApply] = useState(null);

  const runPreview = async () => {
    setLoadingPreview(true);
    setLastApply(null);
    try {
      const { data } = await api.get(`/admin/callers/${callerId}/consolidate-duplicates`);
      setPreview(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoadingPreview(false);
    }
  };

  const runApply = async () => {
    setApplying(true);
    try {
      const { data } = await api.post(
        `/admin/callers/${callerId}/consolidate-duplicates`,
        null,
        { params: { confirm: "CONSOLIDATE", backup: backupOn ? 1 : 0 } },
      );
      setLastApply(data);
      setPreview(null);
      setApplyOpen(false);
      setConfirmText("");
      toast.success(
        `Consolidated — merged ${data.merged_groups} groups, absorbed ${data.absorbed_records} records`
      );
      if (onChanged) onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setApplying(false);
    }
  };

  const confirmOK = confirmText.trim().toUpperCase() === "CONSOLIDATE";
  const nothingToMerge = preview && preview.would_merge_groups === 0;

  return (
    <div
      className="mt-4 border-t border-slate-100 pt-4 space-y-3"
      data-testid={`admin-caller-consolidation-${callerId}`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <div className="text-xs font-bold uppercase tracking-wider text-slate-600">
          De-duplicate donors
        </div>
        <span className="text-[11px] text-slate-500">
          Retro-consolidate same-person records already in this caller&rsquo;s DB
        </span>
      </div>
      <p className="text-[11px] text-slate-500 leading-snug">
        Groups this caller&rsquo;s donors by exact normalized name + city + state. Same-person
        groups on distinct lists collapse into one canonical record (memberships unioned,
        giving preserved, timelines merged). Groups sharing a list are flagged, not merged.
        Nickname-only pairs (e.g. Rob vs Robert) are counted, not merged &mdash; those need
        a separate confirmed pass. <strong>Preview writes nothing.</strong> Apply is guarded
        by a typed confirmation and snapshots every donor before writing by default.
      </p>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          onClick={runPreview}
          disabled={loadingPreview}
          className="bg-flag-navy hover:bg-flag-navy/90 text-white"
          data-testid={`admin-caller-consolidation-preview-btn-${callerId}`}
        >
          {loadingPreview ? "Loading preview…" : "Preview de-duplication"}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => setApplyOpen(true)}
          disabled={!preview || nothingToMerge || applying}
          data-testid={`admin-caller-consolidation-apply-open-${callerId}`}
        >
          Apply consolidation…
        </Button>
        {preview && (
          <span className="text-[11px] text-slate-500">
            Preview run at {new Date().toLocaleTimeString()}
          </span>
        )}
      </div>

      {preview && (
        <div
          className="rounded-lg border-2 border-slate-200 bg-slate-50 p-3 space-y-2 text-sm"
          data-testid={`admin-caller-consolidation-preview-${callerId}`}
        >
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1 text-[13px]">
            <div><span className="font-semibold">Scanned:</span> {preview.scanned_donors.toLocaleString()}</div>
            <div><span className="font-semibold">Groups:</span> {preview.groups_scanned.toLocaleString()}</div>
            <div>
              <span className="font-semibold text-emerald-700">Would merge:</span>{" "}
              <span data-testid={`admin-caller-consolidation-would-merge-${callerId}`}>
                {preview.would_merge_groups} groups / {preview.would_absorb_records} records
              </span>
            </div>
            <div>
              <span className="font-semibold text-amber-700">Would flag:</span>{" "}
              {preview.would_flag_ambiguous_groups} groups / {preview.would_flag_records} records
            </div>
            <div>
              <span className="font-semibold text-slate-700">Nickname pairs:</span>{" "}
              {preview.nickname_variant_pairs_not_merged} (not merged)
            </div>
            <div>
              <span className="font-semibold">Final count:</span>{" "}
              {preview.final_estimated_donor_count?.toLocaleString?.() ?? "—"}
            </div>
          </div>

          {typeof preview.estimated_potential_giving_loss === "number" && (
            <div
              className={`rounded border px-2 py-1 text-[12px] ${
                preview.estimated_potential_giving_loss > 0
                  ? "border-amber-300 bg-amber-50 text-amber-900"
                  : "border-emerald-300 bg-emerald-50 text-emerald-900"
              }`}
              data-testid={`admin-caller-consolidation-loss-${callerId}`}
            >
              <div className="font-semibold">
                Estimated potential lifetime-giving loss:{" "}
                <span className="tabular-nums">
                  ${preview.estimated_potential_giving_loss.toLocaleString(undefined, {maximumFractionDigits: 2})}
                </span>
                {"  "}
                <span className="font-normal">
                  · {preview.groups_with_conflicting_total_given ?? 0} group(s)
                  with conflicting total_given
                </span>
              </div>
              <div className="mt-0.5 text-[11px] font-normal opacity-90">
                Pessimistic upper bound. The merge picks ONE copy&rsquo;s
                giving scalars wholesale — this is the sum of every
                non-chosen copy&rsquo;s positive <code>total_given</code>.
                $0 means the pick-one rule is provably safe on the data;
                any positive number means at least some conflicting
                totals exist and should be spot-checked in the sample
                merges below.
              </div>
            </div>
          )}

          {preview.sample_merges?.length > 0 && (
            <details className="text-[12px]" data-testid={`admin-caller-consolidation-merges-${callerId}`}>
              <summary className="cursor-pointer font-semibold text-emerald-800">
                Sample merges ({preview.sample_merges.length} shown)
              </summary>
              <ul className="mt-1 divide-y divide-slate-200 border border-slate-200 rounded bg-white">
                {preview.sample_merges.map((m, i) => (
                  <li key={i} className="p-2">
                    <div className="font-mono text-[11px] text-slate-700">
                      {m.match_key.join(" / ")}
                    </div>
                    <div className="text-slate-500">
                      Keep <code className="text-[10px]">{(m.canonical_id || "").slice(0, 8)}</code>{" "}
                      &middot; absorb {m.absorbed_ids?.length ?? 0} record(s){" "}
                      &middot; memberships → {(m.memberships_after || []).length}
                    </div>
                  </li>
                ))}
              </ul>
            </details>
          )}

          {preview.sample_flags?.length > 0 && (
            <details className="text-[12px]" data-testid={`admin-caller-consolidation-flags-${callerId}`}>
              <summary className="cursor-pointer font-semibold text-amber-800">
                Ambiguous groups (flagged, not merged) &mdash; {preview.sample_flags.length} shown
              </summary>
              <ul className="mt-1 divide-y divide-slate-200 border border-slate-200 rounded bg-white">
                {preview.sample_flags.map((f, i) => (
                  <li key={i} className="p-2">
                    <div className="font-mono text-[11px] text-slate-700">
                      {f.match_key.join(" / ")}
                    </div>
                    <div className="text-slate-500">
                      {f.record_ids?.length ?? 0} records &middot; reason: <code>{f.reason}</code>
                    </div>
                  </li>
                ))}
              </ul>
            </details>
          )}

          {preview.sample_nickname_pairs?.length > 0 && (
            <details className="text-[12px]" data-testid={`admin-caller-consolidation-nicknames-${callerId}`}>
              <summary className="cursor-pointer font-semibold text-slate-800">
                Nickname pairs (not merged by design) &mdash; {preview.sample_nickname_pairs.length} shown
              </summary>
              <ul className="mt-1 divide-y divide-slate-200 border border-slate-200 rounded bg-white">
                {preview.sample_nickname_pairs.map((p, i) => (
                  <li key={i} className="p-2">
                    <div className="font-mono text-[11px] text-slate-700">
                      {p.first_names.join(" ↔ ")} &middot; {p.key.join(" / ")}
                    </div>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}

      {lastApply && (
        <div
          className="rounded-lg border-2 border-emerald-300 bg-emerald-50 p-3 text-sm"
          data-testid={`admin-caller-consolidation-last-apply-${callerId}`}
        >
          <div className="font-semibold text-emerald-800">Consolidation applied</div>
          <div className="mt-1 text-[13px] text-emerald-900">
            Merged {lastApply.merged_groups} groups &middot; absorbed {lastApply.absorbed_records} records &middot;{" "}
            flagged {lastApply.flagged_ambiguous_groups} groups &middot; final count {lastApply.final_donor_count}
          </div>
          {lastApply.backup_snapshot_id && (
            <div className="mt-1 text-[11px] text-emerald-800 font-mono break-all">
              Snapshot id: {lastApply.backup_snapshot_id}
            </div>
          )}
        </div>
      )}

      <AlertDialog open={applyOpen} onOpenChange={(o) => { if (!applying) { setApplyOpen(o); if (!o) setConfirmText(""); } }}>
        <AlertDialogContent data-testid={`admin-caller-consolidation-apply-dialog-${callerId}`}>
          <AlertDialogHeader>
            <AlertDialogTitle>Apply consolidation?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <div>
                  This will merge <strong>{preview?.would_merge_groups}</strong> same-person groups
                  (absorbing <strong>{preview?.would_absorb_records}</strong> records) and flag{" "}
                  <strong>{preview?.would_flag_ambiguous_groups}</strong> ambiguous groups.
                </div>
                <label className="flex items-center gap-2 text-[13px]">
                  <input
                    type="checkbox"
                    checked={backupOn}
                    onChange={(e) => setBackupOn(e.target.checked)}
                    disabled={applying}
                    data-testid={`admin-caller-consolidation-backup-toggle-${callerId}`}
                  />
                  Snapshot every donor to <code>caller_donors_backup</code> before writing (recommended)
                </label>
                <div className="pt-2">
                  Type <code className="px-1 rounded bg-slate-100 font-bold">CONSOLIDATE</code> to confirm:
                </div>
                <Input
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="CONSOLIDATE"
                  disabled={applying}
                  data-testid={`admin-caller-consolidation-confirm-input-${callerId}`}
                  autoFocus
                />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={applying}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={runApply}
              disabled={!confirmOK || applying}
              className="bg-emerald-700 hover:bg-emerald-800 text-white disabled:opacity-50"
              data-testid={`admin-caller-consolidation-confirm-apply-${callerId}`}
            >
              {applying ? "Applying…" : "Apply consolidation"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}


function CampaignConfigPanel({ callerId }) {
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get(`/admin/callers/${callerId}/campaign-config`)
      .then(({ data }) => { if (!cancelled) setConfig(data); })
      .catch((err) => {
        if (!cancelled) toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      });
    return () => { cancelled = true; };
  }, [callerId]);

  const patch = async (updates) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/callers/${callerId}/campaign-config`, updates);
      setConfig(data);
      toast.success("Campaign config saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!config) {
    return (
      <div className="mt-4 border-t border-slate-100 pt-4 text-sm text-slate-500" data-testid={`admin-caller-campaign-config-loading-${callerId}`}>
        Loading campaign config…
      </div>
    );
  }

  const isStateMatching = config.campaign_type === "state_matching";

  return (
    <div className="mt-4 border-t border-slate-100 pt-4 space-y-3" data-testid={`admin-caller-campaign-config-${callerId}`}>
      <div className="flex flex-wrap items-center gap-2">
        <div className="text-xs font-bold uppercase tracking-wider text-slate-600">Campaign config</div>
        <span
          className="inline-flex items-center text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-flag-navy/10 text-flag-navy"
          data-testid={`admin-caller-campaign-type-badge-${callerId}`}
        >
          {isStateMatching ? "State — Matching Funds" : "Federal"}
        </span>
      </div>
      <p className="text-[11px] text-slate-500 leading-snug">
        Federal is the default and current behavior. Switch to <strong>State — Matching Funds</strong> only for state races with public matching programs — matching-funds contribution buttons will branch on this in a later step. Caller mode is unchanged until then.
      </p>

      {/* Type toggle */}
      <div className="inline-flex rounded-lg border-2 border-slate-200 overflow-hidden" role="radiogroup" aria-label="Campaign type">
        <button
          type="button"
          role="radio"
          aria-checked={!isStateMatching}
          disabled={saving}
          onClick={() => !isStateMatching || patch({ campaign_type: "federal" })}
          data-testid={`admin-caller-campaign-type-federal-${callerId}`}
          className={`px-3 py-1.5 text-sm font-semibold transition-colors ${!isStateMatching ? "bg-flag-navy text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}
        >
          Federal
        </button>
        <button
          type="button"
          role="radio"
          aria-checked={isStateMatching}
          disabled={saving}
          onClick={() => isStateMatching || patch({ campaign_type: "state_matching" })}
          data-testid={`admin-caller-campaign-type-state-matching-${callerId}`}
          className={`px-3 py-1.5 text-sm font-semibold border-l-2 border-slate-200 transition-colors ${isStateMatching ? "bg-flag-navy text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}
        >
          State — Matching Funds
        </button>
      </div>

      {/* Matching-funds fields — only rendered when state_matching. */}
      {isStateMatching && (
        <div className="grid sm:grid-cols-2 gap-3 rounded-lg border-2 border-flag-navy/20 bg-flag-navy/5 p-3" data-testid={`admin-caller-matching-funds-panel-${callerId}`}>
          <div>
            <Label className="text-xs font-semibold text-slate-700">Matchable ceiling (per election)</Label>
            <div className="relative mt-1">
              <span className="absolute inset-y-0 left-3 flex items-center text-slate-500 pointer-events-none text-sm">$</span>
              <Input
                type="number"
                min="0"
                step="1"
                defaultValue={config.matchable_ceiling}
                onBlur={(e) => {
                  const n = e.target.value === "" ? 0 : Number(e.target.value);
                  if (n !== config.matchable_ceiling) patch({ matchable_ceiling: n });
                }}
                className="h-10 text-sm pl-7"
                data-testid={`admin-caller-matchable-ceiling-${callerId}`}
              />
            </div>
            <p className="text-[11px] text-slate-500 mt-1">Max per-donor contribution eligible for match.</p>
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-700">Currently fundraising for</Label>
            <Select
              value={config.current_covered_election}
              onValueChange={(v) => v !== config.current_covered_election && patch({ current_covered_election: v })}
            >
              <SelectTrigger className="mt-1 h-10 text-sm" data-testid={`admin-caller-covered-election-${callerId}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="primary" data-testid={`admin-caller-covered-election-primary-${callerId}`}>Primary</SelectItem>
                <SelectItem value="general" data-testid={`admin-caller-covered-election-general-${callerId}`}>General</SelectItem>
                <SelectItem value="special" data-testid={`admin-caller-covered-election-special-${callerId}`}>Special</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[11px] text-slate-500 mt-1">Which election bucket calls are being made for.</p>
          </div>
        </div>
      )}
    </div>
  );
}

function CallerCard({ caller, onChanged }) {
  const [messageBody, setMessageBody] = useState(caller.template?.message_body || "");
  const [defaultBody, setDefaultBody] = useState("");
  const [saving, setSaving] = useState(false);
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  // Bumped after house/active imports so the CategoriesPanel refetches its
  // per-category counts (a maxed migration changes them without any
  // category-panel action).
  const [catsRefresh, setCatsRefresh] = useState(0);
  // Reference list ("Have Ever Donated") state — separate from the ask list.
  const [refFile, setRefFile] = useState(null);
  const [refImporting, setRefImporting] = useState(false);
  const [refLastResult, setRefLastResult] = useState(null);
  // Active-donors list ("Active Donors Who Haven't Maxed to You").
  const [actFile, setActFile] = useState(null);
  const [actImporting, setActImporting] = useState(false);
  const [actLastResult, setActLastResult] = useState(null);
  // Call Results (staff-only outcome log)
  const [outcomes, setOutcomes] = useState(null);
  const [outcomesOpen, setOutcomesOpen] = useState(false);
  const loadOutcomes = async () => {
    try {
      const { data } = await api.get(bust(`/admin/callers/${caller.id}/outcomes`));
      setOutcomes(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  // Fetch the platform default message once so "Reset to default" can restore it.
  useEffect(() => {
    let cancelled = false;
    api.get("/caller/template/default").then(({ data }) => {
      if (cancelled) return;
      setDefaultBody(data?.message_body || "");
    }).catch(() => { /* non-blocking */ });
    return () => { cancelled = true; };
  }, []);

  const saveTemplate = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/callers/${caller.id}/template`, {
        message_body: messageBody,
      });
      toast.success("Donor Message saved");
      onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const resetToDefault = () => {
    if (!defaultBody) return;
    setMessageBody(defaultBody);
  };

  const upload = async () => {
    if (!file) return;
    setImporting(true);
    setLastResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(
        `/admin/callers/${caller.id}/donors/import`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setLastResult(data);
      const houseParts = [`${(data.created || 0).toLocaleString()} new`, `${(data.updated || 0).toLocaleString()} updated`];
      if (data.needs_review > 0) houseParts.push(`${data.needs_review.toLocaleString()} need review`);
      toast.success(houseParts.join(" · "));
      setFile(null);
      setCatsRefresh((n) => n + 1);
      onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setImporting(false);
    }
  };

  const wipe = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Clear the House list for ${caller.email}?\n\nThis removes the House membership from ${caller.donor_count} donor(s). The donors themselves are KEPT (still in All Donors and on any other lists).`)) return;
    try {
      const { data } = await api.delete(`/admin/callers/${caller.id}/donors`);
      toast.success(`House list cleared — ${data.removed} membership(s) removed, donors kept`);
      setCatsRefresh((n) => n + 1);
      onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const deleteAllDonors = async () => {
    // eslint-disable-next-line no-alert
    const typed = window.prompt(
      `PERMANENTLY DELETE every donor for ${caller.email}?\n\nThis erases ALL ${(caller.all_donor_count ?? caller.donor_count) || 0} donor records, their giving history, notes, and call log — across every list. This cannot be undone.\n\nType DELETE to confirm:`
    );
    if (typed !== "DELETE") {
      if (typed !== null) toast.info("Cancelled — you must type DELETE exactly.");
      return;
    }
    try {
      const { data } = await api.delete(`/admin/callers/${caller.id}/all-donors?confirm=DELETE`);
      toast.success(`Permanently deleted ${data.deleted_donors} donors`);
      setCatsRefresh((n) => n + 1);
      onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const uploadRef = async () => {
    if (!refFile) return;
    setRefImporting(true);
    setRefLastResult(null);
    try {
      const fd = new FormData();
      fd.append("file", refFile);
      const { data } = await api.post(
        `/admin/callers/${caller.id}/reference/import?replace=true`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setRefLastResult(data);
      toast.success(`Imported ${data.inserted.toLocaleString()} reference donors`);
      setRefFile(null);
      onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setRefImporting(false);
    }
  };

  const wipeRef = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete ALL ${caller.reference_count || 0} reference donors for ${caller.email}?`)) return;
    try {
      await api.delete(`/admin/callers/${caller.id}/reference`);
      toast.success("Reference list cleared");
      onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const uploadActive = async () => {
    if (!actFile) return;
    setActImporting(true);
    setActLastResult(null);
    try {
      const fd = new FormData();
      fd.append("file", actFile);
      const { data } = await api.post(
        `/admin/callers/${caller.id}/active-donors/import`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setActLastResult(data);
      const actParts = [`${(data.created || 0).toLocaleString()} new`, `${(data.updated || 0).toLocaleString()} updated`];
      if (data.needs_review > 0) actParts.push(`${data.needs_review.toLocaleString()} need review`);
      toast.success(actParts.join(" · "));
      setActFile(null);
      setCatsRefresh((n) => n + 1);
      onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setActImporting(false);
    }
  };

  const wipeActive = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete ALL ${caller.active_count || 0} active donors for ${caller.email}?`)) return;
    try {
      await api.delete(`/admin/callers/${caller.id}/active-donors`);
      toast.success("Active list cleared");
      onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const fmtImport = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "never");

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5" data-testid={`admin-caller-${caller.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="font-display text-lg font-black text-flag-navy">
            {caller.name}
            {caller.is_demo && (
              <span className="ml-2 align-middle text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-purple-600 text-white" data-testid={`admin-caller-demo-badge-${caller.id}`}>
                Demo
              </span>
            )}
          </div>
          <div className="text-xs text-slate-500">{caller.email}</div>
        </div>
        <div className="flex gap-4 text-right flex-wrap">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Active list</div>
            <div className="font-display text-xl font-black text-flag-red" data-testid={`admin-caller-active-count-${caller.id}`}>
              {(caller.active_count || 0).toLocaleString()}
            </div>
            <div className="text-[10px] text-slate-400">imported {fmtImport(caller.active_last_import_at)}</div>
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">House list</div>
            <div className="font-display text-xl font-black text-flag-navy" data-testid={`admin-caller-count-${caller.id}`}>
              {(caller.donor_count || 0).toLocaleString()}
            </div>
            <div className="text-[10px] text-slate-400">imported {fmtImport(caller.donor_last_import_at)}</div>
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Reference</div>
            <div className="font-display text-xl font-black text-slate-700" data-testid={`admin-caller-ref-count-${caller.id}`}>
              {(caller.reference_count || 0).toLocaleString()}
            </div>
            <div className="text-[10px] text-slate-400">imported {fmtImport(caller.reference_last_import_at)}</div>
          </div>
        </div>
      </div>

      {/* Stage 2 — possible matches held from the last upload(s). Nothing
          is written for these rows until the owner resolves them here. */}
      <ImportReviewQueue
        callerId={caller.id}
        refreshToken={catsRefresh}
        onResolved={() => { setCatsRefresh((n) => n + 1); onChanged(); }}
      />

      <div className="mt-4 border-t border-slate-100 pt-4">
        <div className="text-xs font-bold uppercase tracking-wider text-slate-600">Active list (&ldquo;Active Donors Who Haven&rsquo;t Maxed to You&rdquo; tab)</div>
        <p className="text-[11px] text-slate-500 mt-1 leading-snug">
          House-file donors who also gave to priority federal committees this cycle. Recognized committee columns: Trump, Johnson, Scalise, Smucker, Van Duyne, Stefanik, Langworthy, Lawler (any of these column names, with or without &ldquo;for Congress&rdquo;). Uploads match &amp; update existing active donors (by first + last name, city, state) and add new ones &mdash; nothing is deleted. Donors who hit the $7,000 max move to Maxed Donors automatically.
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <Input
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => setActFile(e.target.files?.[0] || null)}
            className="h-10 max-w-sm"
            data-testid={`admin-caller-act-file-${caller.id}`}
          />
          <Button
            onClick={uploadActive}
            disabled={!actFile || actImporting}
            className="h-10 bg-flag-red hover:bg-flag-red-hover text-white font-bold"
            data-testid={`admin-caller-act-import-${caller.id}`}
          >
            <Upload className="h-4 w-4 mr-1.5" /> {actImporting ? "Importing…" : "Import Active"}
          </Button>
          {(caller.active_count || 0) > 0 && (
            <Button onClick={wipeActive} variant="ghost" className="h-10 text-flag-red font-semibold" data-testid={`admin-caller-act-wipe-${caller.id}`}>
              <Trash2 className="h-4 w-4 mr-1" /> Clear active
            </Button>
          )}
        </div>
        {actLastResult && (
          <div className="mt-2 text-xs text-slate-600" data-testid={`admin-caller-act-import-result-${caller.id}`}>
            <strong className="text-flag-navy">{(actLastResult.created ?? actLastResult.inserted ?? 0).toLocaleString()}</strong> new
            {" · "}<strong className="text-flag-navy">{(actLastResult.updated || 0).toLocaleString()}</strong> updated
            {actLastResult.updated_contact_match > 0 && <> &middot; {actLastResult.updated_contact_match.toLocaleString()} matched by contact info</>}
            {actLastResult.moved_to_maxed > 0 && <> &middot; {actLastResult.moved_to_maxed.toLocaleString()} moved to Maxed Donors</>}
            {actLastResult.created_maxed > 0 && <> &middot; {actLastResult.created_maxed.toLocaleString()} added to Maxed Donors</>}
            {actLastResult.needs_review > 0 && (
              <strong className="text-amber-700" data-testid={`admin-caller-act-review-count-${caller.id}`}>
                {" "}&middot; {actLastResult.needs_review.toLocaleString()} need review (see the review panel above)
              </strong>
            )}
            {actLastResult.skipped_bad > 0 && <> &middot; {actLastResult.skipped_bad.toLocaleString()} skipped (missing name)</>}
            <HeaderMapReport result={actLastResult} testid={`admin-caller-act-headers-${caller.id}`} />
          </div>
        )}
      </div>

      <CategoriesPanel callerId={caller.id} refreshToken={catsRefresh} onImported={() => { setCatsRefresh((n) => n + 1); onChanged(); }} />

      <CampaignConfigPanel callerId={caller.id} />

      <ConsolidationCard callerId={caller.id} onChanged={onChanged} />

      <div className="mt-4 border-t border-slate-100 pt-4">
        <div className="text-xs font-bold uppercase tracking-wider text-slate-600">House list (&ldquo;House File Donors Who Haven&rsquo;t Maxed to You&rdquo; tab)</div>
        <p className="text-[11px] text-slate-500 mt-1 leading-snug">
          Columns accepted (case-insensitive): first_name, last_name (or name), phone, phone_type, <strong>phone 2</strong>, <strong>phone 2 type</strong>, address, city, state, zip, email, employer, occupation, <strong>notes</strong>, first_gift_date, last_gift_date, last_gift_amount, highest_gift, total_given, number_of_gifts, cycle_to_date, <strong>2026 Primary</strong>, <strong>2026 General</strong>, <strong>General Room Remaining</strong>, and any year-tagged Primary/General/CTD columns (e.g. <strong>2024 Primary</strong>, <strong>2022 General</strong>). Modeled/estimated fields are ignored. Re-uploads <strong>match existing donors</strong> by first + last name, city, and state (exact match), update their giving, and never delete anyone &mdash; caller notes and logged outcomes always survive. Donors at or above $7,000 cycle-to-date move to the <strong>Maxed Donors</strong> list automatically (blank cycle-to-date is treated as $0). Prospects with no giving history import normally &mdash; a valid House row needs only a name.
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <Input
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="h-10 max-w-sm"
            data-testid={`admin-caller-file-${caller.id}`}
          />
          <Button
            onClick={upload}
            disabled={!file || importing}
            className="h-10 bg-flag-red hover:bg-flag-red-hover text-white font-bold"
            data-testid={`admin-caller-import-${caller.id}`}
          >
            <Upload className="h-4 w-4 mr-1.5" /> {importing ? "Importing…" : "Import"}
          </Button>
          {caller.donor_count > 0 && (
            <Button onClick={wipe} variant="ghost" className="h-10 text-flag-red font-semibold" data-testid={`admin-caller-wipe-${caller.id}`}>
              <Trash2 className="h-4 w-4 mr-1" /> Clear list
            </Button>
          )}
          {(caller.all_donor_count ?? 0) > 0 && (
            <Button
              onClick={deleteAllDonors}
              variant="ghost"
              className="h-10 text-flag-red font-bold border border-flag-red/30"
              title="Permanently deletes EVERY donor for this client, across all lists"
              data-testid={`admin-caller-delete-all-${caller.id}`}
            >
              <Trash2 className="h-4 w-4 mr-1" /> Delete ALL donors permanently
            </Button>
          )}
        </div>
        {lastResult && (
          <div className="mt-2 text-xs text-slate-600" data-testid={`admin-caller-import-result-${caller.id}`}>
            <strong className="text-flag-navy">{(lastResult.created ?? lastResult.inserted ?? 0).toLocaleString()}</strong> new
            {" · "}<strong className="text-flag-navy">{(lastResult.updated || 0).toLocaleString()}</strong> updated
            {lastResult.updated_contact_match > 0 && <> &middot; {lastResult.updated_contact_match.toLocaleString()} matched by contact info</>}
            {lastResult.moved_to_maxed > 0 && <> &middot; {lastResult.moved_to_maxed.toLocaleString()} moved to Maxed Donors</>}
            {lastResult.created_maxed > 0 && <> &middot; {lastResult.created_maxed.toLocaleString()} added to Maxed Donors</>}
            {lastResult.needs_review > 0 && (
              <strong className="text-amber-700" data-testid={`admin-caller-import-review-count-${caller.id}`}>
                {" "}&middot; {lastResult.needs_review.toLocaleString()} need review (see the review panel above)
              </strong>
            )}
            {lastResult.skipped_bad > 0 && <> &middot; {lastResult.skipped_bad.toLocaleString()} skipped (missing name)</>}
            {typeof lastResult.rows_seen === "number" && (
              <span className="text-slate-400 ml-1">
                &nbsp;· {lastResult.rows_seen.toLocaleString()} rows in file
                {lastResult.rows_seen !== lastResult.accounted_for && (
                  <strong className="text-flag-red ml-1" data-testid={`admin-caller-import-mismatch-${caller.id}`}>
                    — {(lastResult.rows_seen - lastResult.accounted_for).toLocaleString()} unaccounted for
                  </strong>
                )}
              </span>
            )}
            <HeaderMapReport result={lastResult} testid={`admin-caller-headers-${caller.id}`} />
          </div>
        )}
      </div>

      <div className="mt-4 border-t border-slate-100 pt-4">
        <div className="text-xs font-bold uppercase tracking-wider text-slate-600">Reference list (&ldquo;Have Ever Donated&rdquo; tab)</div>
        <p className="text-[11px] text-slate-500 mt-1 leading-snug">
          The full historical donor file. Read-only lookup on the caller side — no call outcomes, no messaging. Uploads replace the previous reference list.
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <Input
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => setRefFile(e.target.files?.[0] || null)}
            className="h-10 max-w-sm"
            data-testid={`admin-caller-ref-file-${caller.id}`}
          />
          <Button
            onClick={uploadRef}
            disabled={!refFile || refImporting}
            className="h-10 bg-flag-navy hover:bg-flag-navy/90 text-white font-bold"
            data-testid={`admin-caller-ref-import-${caller.id}`}
          >
            <Upload className="h-4 w-4 mr-1.5" /> {refImporting ? "Importing…" : "Import reference"}
          </Button>
          {(caller.reference_count || 0) > 0 && (
            <Button onClick={wipeRef} variant="ghost" className="h-10 text-flag-red font-semibold" data-testid={`admin-caller-ref-wipe-${caller.id}`}>
              <Trash2 className="h-4 w-4 mr-1" /> Clear reference
            </Button>
          )}
        </div>
        {refLastResult && (
          <div className="mt-2 text-xs text-slate-600" data-testid={`admin-caller-ref-import-result-${caller.id}`}>
            <strong className="text-flag-navy">{refLastResult.inserted.toLocaleString()}</strong> imported
            {refLastResult.skipped_bad > 0 && <> &middot; {refLastResult.skipped_bad.toLocaleString()} skipped (missing name)</>}
            <HeaderMapReport result={refLastResult} testid={`admin-caller-ref-headers-${caller.id}`} />
          </div>
        )}
      </div>

      <div className="mt-4 border-t border-slate-100 pt-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-600">Call results (staff)</div>
          <Button
            onClick={() => { setOutcomesOpen((v) => !v); if (!outcomes) loadOutcomes(); }}
            variant="outline"
            className="h-8 px-2 text-xs border-2 font-semibold"
            data-testid={`admin-caller-outcomes-toggle-${caller.id}`}
          >
            {outcomesOpen ? "Hide" : "Show"} outcomes
          </Button>
        </div>
        <p className="text-[11px] text-slate-500 mt-1 leading-snug">
          Every call outcome Claudia logs (Reached / Left message / Pledged / Declined / Follow up). Kept out of her main call flow — this is for staff review.
        </p>
        {outcomesOpen && outcomes && (
          <div className="mt-3 space-y-2" data-testid={`admin-caller-outcomes-${caller.id}`}>
            {outcomes.total === 0 && (
              <div className="text-xs text-slate-500 italic">No outcomes logged yet.</div>
            )}
            {outcomes.total > 0 && (
              <>
                <div className="flex flex-wrap gap-2 text-[11px] font-semibold">
                  {Object.entries(outcomes.counts).map(([status, n]) => (
                    <span key={status} className="inline-flex items-center gap-1 bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded">
                      {STATUS_LABEL_MAP[status] || status}: <strong className="text-flag-navy">{n}</strong>
                    </span>
                  ))}
                </div>
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 text-slate-600 text-[10px] uppercase tracking-wider">
                      <tr>
                        <th className="px-2 py-1.5 text-left">Donor</th>
                        <th className="px-2 py-1.5 text-left">Outcome</th>
                        <th className="px-2 py-1.5 text-left">Logged</th>
                        <th className="px-2 py-1.5 text-right">Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {outcomes.outcomes.slice(0, 200).map((o) => (
                        <tr key={o.donor_id} className="border-t border-slate-100">
                          <td className="px-2 py-1.5 font-semibold text-flag-navy">{o.first_name} {o.last_name}</td>
                          <td className="px-2 py-1.5">{STATUS_LABEL_MAP[o.status] || o.status}</td>
                          <td className="px-2 py-1.5 text-slate-500">{fmtDate(o.status_updated_at)}</td>
                          <td className="px-2 py-1.5 text-right text-slate-600">{o.notes_count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      <CallerTeamPanel callerId={caller.id} />

      <div className="mt-4 border-t border-slate-100 pt-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-600">Donor Message</div>
          {defaultBody && messageBody !== defaultBody && (
            <button
              type="button"
              onClick={resetToDefault}
              className="text-[11px] font-bold uppercase tracking-wider text-flag-navy hover:underline"
              data-testid={`admin-caller-msg-reset-${caller.id}`}
            >
              Reset to default
            </button>
          )}
        </div>
        <p className="text-[11px] text-slate-500 mt-1 leading-snug">
          The full message the caller sees on every donor. Edits save server-side and apply to every draft instantly.
        </p>
        <div className="mt-2">
          <Textarea
            value={messageBody}
            onChange={(e) => setMessageBody(e.target.value)}
            rows={14}
            placeholder="Hey [FIRST_NAME]: ..."
            className="text-sm font-mono leading-relaxed"
            spellCheck={true}
            data-testid={`admin-caller-msg-body-${caller.id}`}
          />
          <p className="mt-1.5 text-[11px] text-slate-500 leading-snug" data-testid={`admin-caller-msg-hint-${caller.id}`}>
            You can use{" "}
            <code className="bg-slate-100 px-1 rounded text-[10px] font-mono text-flag-navy">[FIRST_NAME]</code>,{" "}
            <code className="bg-slate-100 px-1 rounded text-[10px] font-mono text-flag-navy">[REMAINING_MAX]</code>, and{" "}
            <code className="bg-slate-100 px-1 rounded text-[10px] font-mono text-flag-navy">[DONATION_LINK]</code>
            {" "}— these fill in automatically per donor.{" "}
            <code className="bg-slate-100 px-1 rounded text-[10px] font-mono text-flag-navy">[REMAINING_MAX]</code>{" "}
            follows the caller&rsquo;s Max / Highest toggle and is formatted as currency ($2,000). Missing values render as blank, not the bracket text. Replace{" "}
            <code className="bg-slate-100 px-1 rounded text-[10px] font-mono text-flag-red">[DONATION_LINK]</code>{" "}
            with this campaign&rsquo;s actual Anedot / ActBlue URL before your team sends any messages.
          </p>
        </div>
        <div className="mt-2 flex items-center gap-2">
          <Button
            onClick={saveTemplate}
            disabled={saving || !messageBody.trim()}
            className="h-10 bg-flag-navy hover:bg-flag-navy/90 text-white font-bold"
            data-testid={`admin-caller-save-${caller.id}`}
          >
            {saving ? "Saving…" : "Save Donor Message"}
          </Button>
        </div>
      </div>
    </div>
  );
}


/* ---------------- Message audit (cross-client donation-URL leak scanner) —
 * Scans every caller template and every category template for hardcoded
 * fundraising URLs (Anedot / ActBlue). Owner reviews the flagged URLs and
 * chooses which to redact — a scrub replaces every occurrence with the
 * `[DONATION_LINK]` placeholder. Backend: GET /admin/callers/message-audit,
 * POST /admin/callers/message-scrub.
 * ------------------------------------------------------------------- */
function MessageAuditPanel() {
  const [state, setState] = useState({ count: null, findings: [] });
  const [loading, setLoading] = useState(false);
  const [scrubbing, setScrubbing] = useState(false);
  const [selected, setSelected] = useState({});  // { url: true }

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(bust("/admin/callers/message-audit"));
      setState(data);
      setSelected({});
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // De-dupe URLs across findings so the checkbox list is compact.
  const uniqueUrls = useMemo(() => {
    const s = new Set();
    (state.findings || []).forEach((f) => (f.urls || []).forEach((u) => s.add(u)));
    return Array.from(s);
  }, [state.findings]);

  const toggle = (u) => setSelected((prev) => ({ ...prev, [u]: !prev[u] }));

  const scrub = async () => {
    const urls = Object.entries(selected).filter(([, v]) => v).map(([k]) => k);
    if (urls.length === 0) {
      toast.error("Select at least one URL to redact.");
      return;
    }
    if (!window.confirm(
      `Redact ${urls.length} URL${urls.length === 1 ? "" : "s"} from EVERY caller and category template? ` +
      `Each occurrence will be replaced with [DONATION_LINK]. This cannot be undone.`
    )) return;
    setScrubbing(true);
    try {
      const { data } = await api.post("/admin/callers/message-scrub", { urls });
      toast.success(
        `Scrubbed ${data.caller_templates_touched} caller template(s) and ${data.categories_touched} category template(s).`
      );
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setScrubbing(false);
    }
  };

  if (state.count === null) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-3 text-xs text-slate-500" data-testid="msg-audit-loading">
        Loading message audit…
      </div>
    );
  }
  if (state.count === 0) {
    return (
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-2.5 text-xs text-emerald-900 flex items-center justify-between" data-testid="msg-audit-clean">
        <span>
          <strong>Message audit clean.</strong> No hardcoded Anedot / ActBlue URLs found in any caller or category template — every campaign&rsquo;s template is either the neutral <code className="bg-white px-1 rounded font-mono text-[10px]">[DONATION_LINK]</code> placeholder or an intentionally-set client-specific link.
        </span>
        <Button variant="ghost" onClick={load} disabled={loading} className="h-7 px-2 text-[11px] font-semibold" data-testid="msg-audit-refresh">
          <RefreshCw className={`h-3 w-3 mr-1 ${loading ? "animate-spin" : ""}`} /> Re-scan
        </Button>
      </div>
    );
  }
  return (
    <div className="bg-amber-50 border-2 border-amber-300 rounded-xl px-4 py-3 space-y-3" data-testid="msg-audit-hits">
      <div className="flex items-start justify-between gap-2">
        <div className="text-sm text-amber-950">
          <strong>{state.count} template{state.count === 1 ? "" : "s"} contain hardcoded fundraising URLs.</strong>
          <p className="text-[12px] mt-0.5 leading-snug">
            Review below. Check any URL that DOESN&rsquo;T belong to that campaign, then <em>Redact selected</em>. Each match gets replaced with <code className="bg-white px-1 rounded font-mono text-[10px]">[DONATION_LINK]</code> and the admin will replace it with the correct URL.
          </p>
        </div>
        <Button variant="ghost" onClick={load} disabled={loading} className="h-8 px-2 text-xs font-semibold" data-testid="msg-audit-refresh">
          <RefreshCw className={`h-3 w-3 mr-1 ${loading ? "animate-spin" : ""}`} /> Re-scan
        </Button>
      </div>

      <div className="bg-white border border-amber-200 rounded-lg overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-amber-100/60 text-amber-950 text-[11px] uppercase tracking-wide">
            <tr>
              <th className="text-left px-3 py-1.5 font-bold">Caller</th>
              <th className="text-left px-3 py-1.5 font-bold">Scope</th>
              <th className="text-left px-3 py-1.5 font-bold">Field</th>
              <th className="text-left px-3 py-1.5 font-bold">URL(s)</th>
            </tr>
          </thead>
          <tbody>
            {(state.findings || []).map((f, i) => (
              <tr
                key={`${f.caller_email}|${f.scope}|${f.category_name || ""}|${f.field}`}
                className="border-t border-amber-100"
                data-testid={`msg-audit-row-${i}`}
              >
                <td className="px-3 py-2 align-top">
                  <div className="font-semibold text-flag-navy">{f.caller_name || "—"}</div>
                  <div className="text-[10px] text-slate-500 truncate">{f.caller_email}</div>
                </td>
                <td className="px-3 py-2 align-top text-slate-700">
                  {f.scope === "category" ? <>Category<br/><span className="text-[10px] text-slate-500">{f.category_name}</span></> : "Caller template"}
                </td>
                <td className="px-3 py-2 align-top text-slate-600 font-mono text-[11px]">{f.field}</td>
                <td className="px-3 py-2 align-top">
                  <ul className="space-y-0.5">
                    {(f.urls || []).map((u) => (
                      <li key={u} className="text-[11px] text-flag-red font-mono break-all">{u}</li>
                    ))}
                  </ul>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="bg-white border border-amber-200 rounded-lg p-3 space-y-2">
        <div className="text-xs font-bold uppercase tracking-wider text-amber-950">Select URLs to redact</div>
        <p className="text-[11px] text-slate-600 leading-snug">
          A URL is safe to redact if you can confirm it doesn&rsquo;t belong to the campaign whose template contains it. Redacting replaces every occurrence — across ALL campaigns and lists — with <code className="bg-slate-100 px-1 rounded font-mono text-[10px]">[DONATION_LINK]</code>.
        </p>
        <ul className="space-y-1">
          {uniqueUrls.map((u) => (
            <li key={u} className="flex items-start gap-2 text-[11px]" data-testid={`msg-audit-url-${u}`}>
              <input
                type="checkbox"
                checked={!!selected[u]}
                onChange={() => toggle(u)}
                className="mt-0.5 h-3.5 w-3.5"
                data-testid={`msg-audit-check-${u}`}
              />
              <label className="font-mono text-flag-red break-all cursor-pointer" onClick={() => toggle(u)}>{u}</label>
            </li>
          ))}
        </ul>
        <div className="flex items-center gap-2 pt-1">
          <Button
            onClick={scrub}
            disabled={scrubbing || Object.values(selected).every((v) => !v)}
            className="h-9 bg-flag-red hover:bg-flag-red-hover text-white font-bold"
            data-testid="msg-audit-scrub"
          >
            {scrubbing ? "Redacting…" : "Redact selected"}
          </Button>
          <div className="text-[11px] text-slate-500">
            {Object.values(selected).filter(Boolean).length} of {uniqueUrls.length} selected
          </div>
        </div>
      </div>
    </div>
  );
}


/* ---------------- Demo account (isolated sales-demo caller) ----------
 * ONE dedicated caller user (`callerdemo@electableai.com`) with fake data.
 * Owner-only. Never runs on deploy — click "Create demo" or "Reset demo
 * data" to fire. Reset rotates the password + wipes/reseeds the demo's
 * data, scoped by that user_id ONLY. Zero risk of touching real clients.
 * Backend: GET /api/admin/demo/status, POST /api/admin/demo/reset.
 * ------------------------------------------------------------------- */
function DemoAccountPanel() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [creds, setCreds] = useState(null); // { email, password } — shown once

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(bust("/admin/demo/status"));
      setStatus(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const doReset = async () => {
    if (status?.exists && !window.confirm(
      status?.fixed_password
        ? "Reset the demo account? This wipes the demo's current data and reseeds the starter fake lists (8 call donors + 5 prospects). The fixed password stays the same. No other account is touched."
        : "Reset the demo account? This will wipe the demo's current data, reseed fresh fake donors, and rotate its password. No other account is touched."
    )) return;
    setResetting(true);
    try {
      const { data } = await api.post("/admin/demo/reset");
      setCreds({ email: data.email, password: data.password, fixed: !!data.fixed_password });
      toast.success(status?.exists ? "Demo reset — fresh starter data seeded." : "Demo account created.");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="bg-white border-2 border-dashed border-purple-300 rounded-xl px-4 py-3 space-y-3" data-testid="admin-demo-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-purple-800">Demo account (sales demos)</div>
          <p className="text-[12px] text-slate-600 mt-0.5 leading-snug max-w-2xl">
            One isolated caller account for demos and safe testing — fully separated from real client data. It appears in the Clients list above like any client, so you can upload and RE-UPLOAD lists into it with the normal importer. Reset restores the starter fake data (8 Oregon call-list donors + 5 prospects). It&rsquo;s auto-created on fresh deployments, so it exists in production too.
          </p>
        </div>
        <Button
          onClick={doReset}
          disabled={resetting || loading}
          className={`h-9 font-bold text-white ${status?.exists ? "bg-purple-700 hover:bg-purple-800" : "bg-flag-red hover:bg-flag-red-hover"}`}
          data-testid={status?.exists ? "admin-demo-reset" : "admin-demo-create"}
        >
          {resetting ? "Working…" : status?.exists ? "Reset demo data" : "Create demo account"}
        </Button>
      </div>

      {status?.exists && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs text-slate-700" data-testid="admin-demo-stats">
          <div><span className="text-slate-500 block text-[10px] uppercase tracking-wide">Login</span><code className="font-mono">{status.email}</code></div>
          <div><span className="text-slate-500 block text-[10px] uppercase tracking-wide">Candidate</span>{status.candidate_name}</div>
          <div><span className="text-slate-500 block text-[10px] uppercase tracking-wide">Donors</span><strong>{status.donors}</strong></div>
          <div><span className="text-slate-500 block text-[10px] uppercase tracking-wide">Lists</span><strong>{status.categories}</strong></div>
        </div>
      )}

      {creds && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-3" data-testid="admin-demo-creds">
          <div className="text-[11px] font-bold uppercase tracking-wider text-purple-900 mb-1">
            {creds.fixed
              ? "Credentials — fixed password (survives resets and deploys)"
              : "Credentials — copy now, this is the only time you\u2019ll see the password"}
          </div>
          <div className="flex items-center gap-2 text-sm">
            <span className="text-slate-500">Email:</span>
            <code className="font-mono bg-white px-2 py-1 rounded border border-purple-200" data-testid="admin-demo-cred-email">{creds.email}</code>
            <Button variant="ghost" onClick={() => { copyToClipboard(creds.email); toast.success("Email copied."); }} className="h-7 px-2 text-xs font-semibold" data-testid="admin-demo-copy-email">
              <Copy className="h-3 w-3" />
            </Button>
          </div>
          <div className="flex items-center gap-2 text-sm mt-1">
            <span className="text-slate-500">Password:</span>
            <code className="font-mono bg-white px-2 py-1 rounded border border-purple-200" data-testid="admin-demo-cred-password">{creds.password}</code>
            <Button variant="ghost" onClick={() => { copyToClipboard(creds.password); toast.success("Password copied."); }} className="h-7 px-2 text-xs font-semibold" data-testid="admin-demo-copy-password">
              <Copy className="h-3 w-3" />
            </Button>
          </div>
          <p className="text-[11px] text-purple-800 mt-2">Share only with people running product demos. Never with real customers.</p>
        </div>
      )}
    </div>
  );
}


/* ---------------- Caller team (add / remove co-caller users) ----------
 * Backend: GET/POST /api/admin/callers/{userId}/team,
 *          DELETE /api/admin/callers/{userId}/team/{coId}.
 * A co-caller is a fresh user with role="caller" and caller_owner_id set to
 * the primary caller's id — they log in with their own email/password but
 * every scoped caller query resolves to the primary's data. Per-client
 * isolation still holds because we never widen the owner scope on read.
 * ------------------------------------------------------------------- */
function CallerTeamPanel({ callerId }) {
  const [team, setTeam] = useState(null);       // { primary, co_callers }
  const [showAdd, setShowAdd] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(bust(`/admin/callers/${callerId}/team`));
      setTeam(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  useEffect(() => { load(); }, [callerId]);  // eslint-disable-line react-hooks/exhaustive-deps

  const addUser = async () => {
    const clean = { name: name.trim(), email: email.trim().toLowerCase(), password };
    if (!clean.name || !clean.email || clean.password.length < 6) {
      toast.error("Fill in name, email, and a password of at least 6 characters.");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/admin/callers/${callerId}/team`, clean);
      toast.success(`${clean.name} added — they can log in now.`);
      setName(""); setEmail(""); setPassword(""); setShowAdd(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const removeUser = async (coId, coEmail) => {
    if (!window.confirm(`Remove ${coEmail} from this caller account? Their login will stop working immediately.`)) return;
    try {
      await api.delete(`/admin/callers/${callerId}/team/${coId}`);
      toast.success("User removed.");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const coList = team?.co_callers || [];

  return (
    <div className="mt-4 border-t border-slate-100 pt-4" data-testid={`admin-caller-team-${callerId}`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-xs font-bold uppercase tracking-wider text-slate-600">
          Additional users on this account
        </div>
        <Button
          onClick={() => setShowAdd((v) => !v)}
          variant="outline"
          className="h-8 px-2 text-xs border-2 font-semibold"
          data-testid={`admin-caller-team-toggle-${callerId}`}
        >
          <UserPlus className="h-3 w-3 mr-1" /> {showAdd ? "Cancel" : "Add user"}
        </Button>
      </div>
      <p className="text-[11px] text-slate-500 mt-1 leading-snug">
        Additional users log in with their own credentials and share this campaign&rsquo;s call lists, donors, messages, notes, and outcome logs. They have caller-level access only — they can never see any other campaign&rsquo;s data.
      </p>

      {showAdd && (
        <div className="mt-3 bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-2" data-testid={`admin-caller-team-add-${callerId}`}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <Input
              placeholder="Full name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="h-9"
              data-testid={`admin-caller-team-name-${callerId}`}
            />
            <Input
              type="email"
              placeholder="email@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-9"
              data-testid={`admin-caller-team-email-${callerId}`}
            />
            <PasswordInput
              placeholder="Initial password (≥6 chars)"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="h-9"
              minLength={6}
              data-testid={`admin-caller-team-password-${callerId}`}
            />
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={addUser}
              disabled={saving}
              className="h-9 bg-flag-navy hover:bg-flag-navy/90 text-white font-bold"
              data-testid={`admin-caller-team-save-${callerId}`}
            >
              {saving ? "Adding…" : "Add user"}
            </Button>
            <p className="text-[11px] text-slate-500">
              Share the password with them out-of-band. They can change it later from the caller settings.
            </p>
          </div>
        </div>
      )}

      <div className="mt-3">
        {team === null ? (
          <div className="text-[11px] text-slate-500 italic">Loading…</div>
        ) : coList.length === 0 ? (
          <div className="text-[11px] text-slate-500 italic" data-testid={`admin-caller-team-empty-${callerId}`}>
            Just the primary account for now ({team.primary?.email}). Add teammates above to share access.
          </div>
        ) : (
          <ul className="space-y-1.5" data-testid={`admin-caller-team-list-${callerId}`}>
            {coList.map((co) => (
              <li
                key={co.id}
                className="flex items-center justify-between bg-white border border-slate-200 rounded-md px-3 py-2 text-sm"
                data-testid={`admin-caller-team-row-${co.id}`}
              >
                <div className="min-w-0">
                  <div className="font-semibold text-flag-navy truncate">{co.name}</div>
                  <div className="text-xs text-slate-500 truncate">{co.email}</div>
                </div>
                <Button
                  variant="ghost"
                  onClick={() => removeUser(co.id, co.email)}
                  className="h-8 px-2 text-xs text-flag-red font-semibold hover:bg-red-50"
                  data-testid={`admin-caller-team-remove-${co.id}`}
                >
                  <Trash2 className="h-3 w-3 mr-1" /> Remove
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}


/* ---------------- Legal (acceptance audit log) ---------------- 
 * Owner-only queryable view of the append-only `legal_acceptances`
 * collection. Supports filtering by user (email), by document version,
 * and CSV export. Backend: /api/admin/legal/acceptances(.../export) and
 * /api/admin/legal/documents/{kind}/{version} (full body reproduction).
 * -------------------------------------------------------------- */
function LegalTab() {
  const [versions, setVersions] = useState({ terms: [], privacy: [] });
  const [filters, setFilters] = useState({ user_email: "", kind: "", version: "" });
  const [data, setData] = useState({ total: 0, returned: 0, acceptances: [] });
  const [loading, setLoading] = useState(false);
  const [reproModal, setReproModal] = useState(null); // { kind, version, body_markdown, effective_date, accepted_by_count }

  // Available versions come from GET /legal/{kind}/versions (both terms and
  // privacy) — no need to hit an admin endpoint since these are
  // authenticated user-facing.
  useEffect(() => {
    (async () => {
      try {
        const [t, p] = await Promise.all([
          api.get("/legal/terms/versions"),
          api.get("/legal/privacy/versions"),
        ]);
        setVersions({ terms: t.data.versions || [], privacy: p.data.versions || [] });
      } catch {}
    })();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.user_email.trim()) params.set("user_email", filters.user_email.trim());
      if (filters.kind) params.set("kind", filters.kind);
      if (filters.version) params.set("version", filters.version);
      params.set("limit", "500");
      const { data: d } = await api.get(`/admin/legal/acceptances?${params.toString()}`);
      setData(d);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const exportCsv = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.kind) params.set("kind", filters.kind);
      if (filters.version) params.set("version", filters.version);
      const resp = await api.get(`/admin/legal/acceptances/export?${params.toString()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      a.download = `legal-acceptances-${ts}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV downloaded.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const reproduce = async (kind, version) => {
    try {
      const { data: d } = await api.get(`/admin/legal/documents/${kind}/${version}`);
      setReproModal(d);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const versionOptionsForKind = filters.kind
    ? (versions[filters.kind] || [])
    : [...(versions.terms || []), ...(versions.privacy || [])];

  return (
    <div className="space-y-4" data-testid="admin-legal">
      <div className="bg-flag-navy/5 border border-flag-navy/20 rounded-xl px-4 py-3 text-sm text-slate-700">
        <strong className="text-flag-navy">Legal audit log</strong> — every Terms &amp; Privacy Policy acceptance is recorded here, append-only. Query by user or by document version. Publishing a new version automatically prompts every user to re-accept on their next request via <code className="bg-slate-100 px-1 rounded text-xs">LegalGate</code>. Past document versions are preserved verbatim so any acceptance can be reproduced exactly as the user saw it.
      </div>

      {/* Current published versions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="admin-legal-versions">
        {["terms", "privacy"].map((kind) => {
          const list = versions[kind] || [];
          const current = list[0];
          return (
            <div key={kind} className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div className="text-xs font-bold uppercase tracking-wider text-slate-600">{kind === "terms" ? "Terms of Service" : "Privacy Policy"}</div>
                <span className="text-[10px] font-bold uppercase tracking-wide bg-emerald-100 text-emerald-900 px-2 py-0.5 rounded">Current v{current?.version || "—"}</span>
              </div>
              <div className="mt-1 text-xs text-slate-500">{list.length} version{list.length === 1 ? "" : "s"} on file. Effective {current?.effective_date || "—"}.</div>
              <ul className="mt-2 space-y-1 text-xs">
                {list.map((v) => (
                  <li key={v.version} className="flex items-center justify-between gap-2" data-testid={`admin-legal-ver-${kind}-${v.version}`}>
                    <span className="font-mono">v{v.version} <span className="text-slate-400">·</span> published {v.published_at?.slice(0, 10)}</span>
                    <button
                      type="button"
                      onClick={() => reproduce(kind, v.version)}
                      className="text-flag-navy font-semibold hover:underline"
                      data-testid={`admin-legal-repro-${kind}-${v.version}`}
                    >
                      View body
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-2">Query acceptance log</div>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
          <Input
            placeholder="Filter by user email…"
            value={filters.user_email}
            onChange={(e) => setFilters((f) => ({ ...f, user_email: e.target.value }))}
            className="h-9 sm:col-span-2"
            data-testid="admin-legal-filter-email"
          />
          <select
            value={filters.kind}
            onChange={(e) => setFilters((f) => ({ ...f, kind: e.target.value, version: "" }))}
            className="h-9 rounded-md border border-slate-300 bg-white px-2 text-sm"
            data-testid="admin-legal-filter-kind"
          >
            <option value="">Any document</option>
            <option value="terms">Terms of Service</option>
            <option value="privacy">Privacy Policy</option>
          </select>
          <select
            value={filters.version}
            onChange={(e) => setFilters((f) => ({ ...f, version: e.target.value }))}
            className="h-9 rounded-md border border-slate-300 bg-white px-2 text-sm"
            data-testid="admin-legal-filter-version"
          >
            <option value="">Any version</option>
            {versionOptionsForKind.map((v) => (
              <option key={`${filters.kind || "any"}-${v.version}`} value={v.version}>v{v.version}</option>
            ))}
          </select>
        </div>
        <div className="mt-2 flex items-center gap-2">
          <Button onClick={load} disabled={loading} className="h-9 bg-flag-navy hover:bg-flag-navy/90 text-white font-bold" data-testid="admin-legal-apply">
            <RefreshCw className={`h-3 w-3 mr-1 ${loading ? "animate-spin" : ""}`} /> Apply
          </Button>
          <Button
            onClick={() => { setFilters({ user_email: "", kind: "", version: "" }); setTimeout(load, 0); }}
            variant="outline"
            className="h-9 border-2 font-semibold"
            data-testid="admin-legal-clear"
          >
            Clear filters
          </Button>
          <div className="flex-1" />
          <Button onClick={exportCsv} variant="outline" className="h-9 border-2 font-semibold" data-testid="admin-legal-export">
            <Download className="h-3 w-3 mr-1" /> Export CSV
          </Button>
        </div>
      </div>

      {/* Results */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-4 py-2 border-b border-slate-100 text-xs text-slate-500" data-testid="admin-legal-count">
          Showing {data.returned} of {data.total} acceptance{data.total === 1 ? "" : "s"}
        </div>
        {data.acceptances.length === 0 ? (
          <div className="p-8 text-center text-slate-500 text-sm" data-testid="admin-legal-empty">
            No acceptances match these filters.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-600 text-[10px] uppercase tracking-wider">
                <tr>
                  <th className="text-left px-3 py-2 font-bold">User</th>
                  <th className="text-left px-3 py-2 font-bold">Role</th>
                  <th className="text-left px-3 py-2 font-bold">Document</th>
                  <th className="text-left px-3 py-2 font-bold">Accepted at (UTC)</th>
                  <th className="text-left px-3 py-2 font-bold">IP</th>
                </tr>
              </thead>
              <tbody data-testid="admin-legal-rows">
                {data.acceptances.map((a) => (
                  <tr key={a.id} className="border-t border-slate-100" data-testid={`admin-legal-row-${a.id}`}>
                    <td className="px-3 py-2 align-top">
                      <div className="font-semibold text-flag-navy">{a.user_name || "—"}</div>
                      <div className="text-[10px] text-slate-500 truncate max-w-[200px]">{a.user_email}</div>
                    </td>
                    <td className="px-3 py-2 align-top text-slate-600">{a.user_role || "owner/candidate"}</td>
                    <td className="px-3 py-2 align-top">
                      <div className="font-semibold">{a.kind === "terms" ? "Terms of Service" : "Privacy Policy"} <span className="text-slate-400">v{a.version}</span></div>
                      <div className="text-[10px] text-slate-500">effective {a.document_effective_date}</div>
                    </td>
                    <td className="px-3 py-2 align-top font-mono text-[11px] text-slate-600">{a.accepted_at?.replace("T", " ").slice(0, 19)}</td>
                    <td className="px-3 py-2 align-top font-mono text-[11px] text-slate-500">{a.ip || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {reproModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setReproModal(null)} data-testid="admin-legal-repro-modal">
          <div className="bg-white max-w-3xl w-full max-h-[85vh] rounded-xl shadow-2xl flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
              <div>
                <div className="font-display font-bold text-flag-navy">
                  {reproModal.kind === "terms" ? "Terms of Service" : "Privacy Policy"} v{reproModal.version}
                </div>
                <div className="text-xs text-slate-500">
                  Effective {reproModal.effective_date} · Published {reproModal.published_at?.slice(0, 10)} · Accepted by {reproModal.accepted_by_count} user{reproModal.accepted_by_count === 1 ? "" : "s"}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setReproModal(null)}
                className="text-slate-500 hover:text-flag-navy text-2xl leading-none"
                data-testid="admin-legal-repro-close"
              >
                ×
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-4">
              <pre className="whitespace-pre-wrap font-sans text-sm text-slate-800 leading-relaxed" data-testid="admin-legal-repro-body">
                {reproModal.body_markdown}
              </pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


/* ---------------- Resets ---------------- */
function ResetsTab() {
  const [rows, setRows] = useState([]);
  const load = async () => {
    try { const { data } = await api.get("/admin/password-resets"); setRows(data || []); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };
  useEffect(() => { load(); }, []);
  return (
    <div className="space-y-3" data-testid="admin-resets">
      <p className="text-sm text-slate-600">
        Reset links are now <strong>emailed automatically</strong> when users hit <Link to="/forgot-password" className="text-flag-navy font-semibold hover:underline">Forgot password</Link>. This page is the backup view — every link is still recorded here in case email delivery fails or a user reports not receiving it.
      </p>
      <Button onClick={load} variant="outline" className="h-9 border-2 font-semibold" data-testid="admin-resets-refresh-btn"><RefreshCw className="h-3 w-3 mr-1" />Refresh</Button>
      {rows.length === 0 ? <div className="bg-white border border-dashed border-slate-300 rounded-xl p-8 text-center text-slate-500 text-sm" data-testid="admin-resets-empty">No active reset links.</div> : (
        <ul className="space-y-2">
          {rows.map((r) => (
            <li key={r.created_at + r.user_id} className="bg-white border border-slate-200 rounded-xl shadow-sm p-4" data-testid={`admin-reset-row-${r.user_id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <div className="font-semibold text-flag-navy">{r.email}</div>
                  <div className="text-xs text-slate-500">Created {fmtDate(r.created_at)} · Expires {fmtDate(r.expires_at)}</div>
                </div>
                <Button onClick={() => copyToClipboard(r.link)} variant="outline" className="h-9 border-2 font-semibold" data-testid={`admin-reset-copy-${r.user_id}`}><Copy className="h-3 w-3 mr-1" />Copy link</Button>
              </div>
              <div className="mt-2 bg-slate-50 border border-slate-200 rounded p-2 font-mono text-xs text-slate-700 break-all" data-testid={`admin-reset-link-${r.user_id}`}>{r.link}</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
